import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getDailyVerse } from './services/dailyVerse';
import { createPrayer, getFallbackPrayer } from './services/aiPrayer';
import {
  shouldShowReminderPrompt,
  dismissReminderPrompt,
  enableDailyReminders,
  disableDailyReminders,
  remindersEnabled,
  isNotificationsSupported,
  getReminderTimes,
  saveReminderTimes,
  rescheduleDailyReminders,
} from './services/notifications';
import {
  canUseAiPrayer,
  aiPrayersRemaining,
  recordAiPrayerUse,
  freeAllowance,
  getTier,
  FREE_AI_PRAYER_LIMIT,
  purchase as purchasePlus,
  restore as restorePlus,
  refreshTier,
  canPurchaseOnWeb,
  getPlusPlanPricing,
  getSubscriptionManagementUrl,
  identifyPurchasesUser,
  resetPurchasesUser,
  FALLBACK_PLUS_PLANS,
  PRODUCT_ANNUAL,
} from './services/entitlements';
import {
  canUseAccounts,
  consumeAccountReturn,
  emailSignInCode,
  getCurrentAccount,
  normalizeAccountEmail,
  requestAccountDeletion,
  signOutAccount,
  verifyEmailSignInCode,
  watchAccount,
} from './services/account';
import {
  THEMED_PRAYERS,
  THEME_ORDER,
  BIBLICAL_PRAYERS,
  MORNING_PRAYER,
  EVENING_PRAYER,
} from './data/prayers';
import { getArtworkOfDay } from './data/artworks';
import {
  canSpeak,
  speak,
  stopSpeaking,
  canListen,
  startListening,
  stopListening,
  usesEnhancedVoice,
} from './services/speech';
import { shareVerse, sharePrayer, isPersonalPrayer } from './services/sharing';
import {
  getIntentions,
  rememberIntention,
  markPrayed,
  resolveIntention,
} from './services/intentions';
import { BENEDICTIONS, pickBenediction } from './services/benedictions';
import { needsUrgentSupport, URGENT_SUPPORT_MESSAGE } from './services/safety';
import { clearPrivateContent, exportBackupFile, restoreBackupFile } from './services/backup';
import './App.css';

// ============================================
// BELOVED - A daily companion for prayer
// Broad-Christian. Built for calm, simple use.
// ============================================
//
// Four places to be:
//   Today  - a greeting, today's scripture, and a few large buttons
//   Pray   - morning & evening prayer, prayers by theme, prayers from the Bible
//   Saved  - the prayers and verses you have kept
//   Settings - reading size, reminders, private backup, privacy, and purchases
//
// "Help Me Pray" is a gentle three-step journey that writes a prayer with you.
//
// Content lives in src/data/ (verses.js, prayers.js). Prayers you request are
// written by src/services/aiPrayer.js, which always returns a real prayer.
// ============================================

// --------------------------------------------
// Colours (dark, reverent, high-contrast for older eyes)
// --------------------------------------------
const COLORS = {
  bg: '#0D0D12',
  bgCard: '#16161D',
  bgLight: '#1E1E28',
  gold: '#D4AF37',
  goldLight: '#E8C96B',
  cream: '#F4E4C1',
  text: '#F8F5F0', // body text, very high contrast
  secondary: '#D8D2C6', // secondary text, still >= 4.5:1 on the dark background
  border: 'rgba(212, 175, 55, 0.28)',
};
const PUBLIC_SITE_ORIGIN = 'https://belovedapp.net';
// Stripe only shows Apple Pay after the live host is registered as a Payment
// Method Domain. Keep launch copy truthful until that dashboard check is done.
const APPLE_PAY_DOMAIN_VERIFIED =
  (process.env.REACT_APP_APPLE_PAY_DOMAIN_VERIFIED || '').trim().toLowerCase() === 'true';

const SERIF = "'Georgia', serif";

// A faint static golden halo for gold headings and the header wordmark. Applied
// to controls and headings only — never behind body or prayer text.
const GOLD_HALO = '0 0 24px rgba(212,175,55,0.35)';

/**
 * Whether the person has asked the system to reduce motion. Feature-detects
 * matchMedia (absent in jsdom) and never throws — falls back to "no" so tests
 * and old browsers simply get the full, gentle experience.
 * @returns {boolean}
 */
function prefersReducedMotion() {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches === true
    );
  } catch (e) {
    return false;
  }
}

// --------------------------------------------
// Text size (accessibility)
// --------------------------------------------
// One preference multiplies the app's core font sizes so someone with tired
// eyes can make everything bigger with a single tap. The extra-large option is
// particularly important in the Capacitor web view, where Beloved provides its
// own explicit reading control.
const TEXT_SIZE_SCALE = { comfortable: 1, large: 1.3, extraLarge: 1.6 };

// The scale currently in force. It is set once at the top of each render of the
// single app instance (see BelovedApp) and read synchronously by `scaleFont`
// throughout that same render, so every surface — headings, body, buttons,
// prayer text, nav — grows together. Kept module-level so both the standalone
// splash/prayer components and the shared style objects can honour it without
// threading a prop through every element.
let activeTextScale = 1;

/**
 * Scale a base font size (in px) by the current text-size preference.
 * @param {number} px the comfortable-size value in pixels
 * @returns {string} a CSS px string, rounded to a whole pixel
 */
function scaleFont(px) {
  return `${Math.round(px * activeTextScale)}px`;
}

// --------------------------------------------
// Simple inline line icons (no emoji anywhere in the UI)
// --------------------------------------------
const ICON_PATHS = {
  today: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <line x1="12" y1="2.5" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="21.5" />
      <line x1="2.5" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="21.5" y2="12" />
      <line x1="5.2" y1="5.2" x2="6.9" y2="6.9" />
      <line x1="17.1" y1="17.1" x2="18.8" y2="18.8" />
      <line x1="5.2" y1="18.8" x2="6.9" y2="17.1" />
      <line x1="17.1" y1="6.9" x2="18.8" y2="5.2" />
    </>
  ),
  pray: (
    <>
      <line x1="12" y1="3" x2="12" y2="21" />
      <line x1="6.5" y1="8.5" x2="17.5" y2="8.5" />
    </>
  ),
  saved: <path d="M18.5 21l-6.5-4.6L5.5 21V5.5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2z" />,
  heart: (
    <path d="M20.4 5.2a5 5 0 0 0-7.1 0L12 6.5l-1.3-1.3a5 5 0 1 0-7.1 7.1l1.3 1.3L12 21l7.1-7.4 1.3-1.3a5 5 0 0 0 0-7.1z" />
  ),
  health: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  cloud: <path d="M17.5 19a4.3 4.3 0 0 0 0-8.6 6 6 0 0 0-11.6 1.5A3.9 3.9 0 0 0 6.5 19z" />,
  home: (
    <>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M5.5 9.3V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.3" />
    </>
  ),
  droplet: <path d="M12 3.4 6.6 10.6a6.4 6.4 0 1 0 10.8 0z" />,
  moon: <path d="M20.5 13A8.5 8.5 0 1 1 11 3.5 6.7 6.7 0 0 0 20.5 13z" />,
  compass: (
    <>
      <circle cx="12" cy="12" r="8.8" />
      <polygon points="15.6 8.4 10.9 10.9 8.4 15.6 13.1 13.1" />
    </>
  ),
  star: (
    <polygon points="12 3.2 14.5 9 20.7 9.5 16 13.6 17.5 19.7 12 16.4 6.5 19.7 8 13.6 3.3 9.5 9.5 9" />
  ),
  sunrise: (
    <>
      <path d="M17 18a5 5 0 0 0-10 0" />
      <line x1="12" y1="2.5" x2="12" y2="9" />
      <line x1="3.5" y1="18" x2="1.5" y2="18" />
      <line x1="22.5" y1="18" x2="20.5" y2="18" />
      <line x1="4.8" y1="10.8" x2="6.2" y2="12.2" />
      <line x1="19.2" y1="10.8" x2="17.8" y2="12.2" />
      <polyline points="8.5 6 12 2.5 15.5 6" />
      <line x1="2" y1="21.5" x2="22" y2="21.5" />
    </>
  ),
  book: (
    <>
      <path d="M12 6.2c-1.6-1-4-1.7-6.5-1.7V18c2.5 0 4.9.7 6.5 1.7" />
      <path d="M12 6.2c1.6-1 4-1.7 6.5-1.7V18c-2.5 0-4.9.7-6.5 1.7" />
    </>
  ),
  chevron: <polyline points="9.5 5.5 16 12 9.5 18.5" />,
  back: (
    <>
      <line x1="19" y1="12" x2="5.5" y2="12" />
      <polyline points="12 18.5 5.5 12 12 5.5" />
    </>
  ),
  check: <polyline points="4.5 12.5 9.5 17.5 19.5 6.5" />,
  cross: (
    <>
      <line x1="12" y1="3.5" x2="12" y2="20.5" />
      <line x1="6.5" y1="9" x2="17.5" y2="9" />
    </>
  ),
  speaker: (
    <>
      <polygon points="4 9 4 15 8 15 13 19 13 5 8 9" />
      <path d="M16.5 8.5a5 5 0 0 1 0 7" />
      <path d="M19.5 6a8 8 0 0 1 0 12" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6 11a6 6 0 0 0 12 0" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <line x1="9" y1="21" x2="15" y2="21" />
    </>
  ),
  share: (
    <>
      <circle cx="18" cy="5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="19" r="2.5" />
      <line x1="8.2" y1="10.8" x2="15.8" y2="6.2" />
      <line x1="8.2" y1="13.2" x2="15.8" y2="17.8" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
};

/**
 * A simple line icon.
 * @param {{name: string, size?: number, color?: string, strokeWidth?: number}} props
 * @returns {JSX.Element}
 */
const Icon = ({ name, size = 24, color = 'currentColor', strokeWidth = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {ICON_PATHS[name]}
  </svg>
);

// Which icon represents each prayer theme.
const THEME_ICON = {
  health: 'health',
  family: 'home',
  grief: 'droplet',
  worry: 'cloud',
  gratitude: 'heart',
  sleep: 'moon',
  guidance: 'compass',
  thanks: 'star',
};

// The six large choices on the first "Help Me Pray" step, each mapped to a
// prayer theme in the data.
const HELP_OPTIONS = [
  { key: 'health', label: 'My health', icon: 'health', theme: 'health' },
  { key: 'love', label: 'Someone I love', icon: 'heart', theme: 'family' },
  { key: 'worry', label: 'Worry or sadness', icon: 'cloud', theme: 'worry' },
  { key: 'family', label: 'Family', icon: 'home', theme: 'family' },
  { key: 'grief', label: 'Grief or loss', icon: 'droplet', theme: 'grief' },
  { key: 'else', label: 'Something else', icon: 'compass', theme: 'guidance' },
];

// --------------------------------------------
// Small helpers
// --------------------------------------------

/**
 * A time-of-day greeting, with no personal name.
 * @param {Date} [date]
 * @returns {string}
 */
function getGreeting(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Whether evening prayer suits the current time (late afternoon onward).
 * @param {Date} [date]
 * @returns {boolean}
 */
function isEveningTime(date = new Date()) {
  return date.getHours() >= 17;
}

// The web landing page at `/` already introduces Beloved. Going into `/app`
// should therefore feel immediate, while the bundled native app keeps this
// gentle opening moment.
function shouldShowWelcomeMoment() {
  if (typeof window === 'undefined') return true;
  return !/^\/app(?:\/|$)/.test(window.location.pathname || '');
}

function safeHttpsUrl(value) {
  try {
    const url = new URL(String(value || ''));
    return url.protocol === 'https:' ? url.toString() : '';
  } catch (error) {
    return '';
  }
}

// --------------------------------------------
// Welcome moment (user-controlled, so the scripture is never rushed)
// --------------------------------------------
const WelcomeScreen = ({ image, onComplete }) => {
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    const fadeIn = setTimeout(() => setOpacity(1), 80);
    return () => clearTimeout(fadeIn);
  }, []);

  if (!image) return null;

  return (
    <div
      onClick={onComplete}
      role="button"
      tabIndex={0}
      aria-label="Continue to Beloved"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onComplete();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: COLORS.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        opacity,
        transition: 'opacity 0.45s ease-in-out',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${image.file})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.4,
          filter: 'blur(2px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(13,13,18,0.35) 0%, rgba(13,13,18,0.65) 50%, rgba(13,13,18,0.92) 100%)',
        }}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          padding: '40px 30px',
          maxWidth: '400px',
        }}
      >
        <div style={{ color: COLORS.gold, marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
          <Icon name="cross" size={28} strokeWidth={1.6} />
        </div>
        <h1
          style={{
            fontSize: `clamp(36px, ${scaleFont(44)}, 56px)`,
            fontWeight: 300,
            color: COLORS.gold,
            letterSpacing: '8px',
            margin: '0 0 26px',
            fontFamily: SERIF,
          }}
        >
          BELOVED
        </h1>
        <p style={{ fontSize: scaleFont(21), fontStyle: 'italic', color: COLORS.cream, lineHeight: 1.7, fontFamily: SERIF }}>
          “{image.scripture}”
        </p>
        <p style={{ fontSize: scaleFont(15), color: COLORS.gold, letterSpacing: '2px', marginTop: '16px' }}>
          {image.reference}
        </p>
        <p style={{ fontSize: scaleFont(18), color: COLORS.cream, lineHeight: 1.55, margin: '28px 0 0' }}>
          Personal prayer and daily Scripture, whenever you need words.
        </p>
        <p style={{ fontSize: scaleFont(15), color: COLORS.secondary, marginTop: '22px' }}>Tap to begin</p>
      </div>
    </div>
  );
};

// --------------------------------------------
// The gold-bordered prayer body (shared by the reader and the Help journey)
// --------------------------------------------
const PrayerBody = ({ text }) => (
  <div
    style={{
      whiteSpace: 'pre-wrap',
      lineHeight: 2,
      fontSize: scaleFont(20),
      color: COLORS.cream,
      fontFamily: SERIF,
      textAlign: 'left',
      padding: '28px 22px',
      border: `1px solid ${COLORS.gold}`,
      borderRadius: '20px',
      background: `linear-gradient(160deg, ${COLORS.bgCard} 0%, ${COLORS.bgLight} 100%)`,
      boxShadow: '0 0 40px rgba(212,175,55,0.10)',
    }}
  >
    {text}
  </div>
);

const PRAYER_LIGHT_MOTES = [
  { x: '-110px', y: '-52px', fx: '-100px', fy: '-72px', size: '5px', delay: '-1.1s', duration: '3.8s' },
  { x: '-78px', y: '70px', fx: '-92px', fy: '54px', size: '7px', delay: '-2.3s', duration: '4.2s' },
  { x: '95px', y: '-64px', fx: '106px', fy: '-44px', size: '6px', delay: '-0.6s', duration: '3.5s' },
  { x: '110px', y: '34px', fx: '96px', fy: '58px', size: '4px', delay: '-1.8s', duration: '4.5s' },
  { x: '-24px', y: '-88px', fx: '-5px', fy: '-96px', size: '8px', delay: '-2.8s', duration: '4.1s' },
  { x: '20px', y: '82px', fx: '42px', fy: '74px', size: '5px', delay: '-0.9s', duration: '3.7s' },
  { x: '-128px', y: '8px', fx: '-116px', fy: '24px', size: '4px', delay: '-3.1s', duration: '4.6s' },
  { x: '126px', y: '-4px', fx: '114px', fy: '-20px', size: '7px', delay: '-1.4s', duration: '4s' },
  { x: '-58px', y: '-20px', fx: '-45px', fy: '-37px', size: '4px', delay: '-2s', duration: '3.4s' },
  { x: '55px', y: '28px', fx: '67px', fy: '12px', size: '5px', delay: '-2.6s', duration: '3.9s' },
  { x: '-10px', y: '54px', fx: '-28px', fy: '62px', size: '6px', delay: '-0.3s', duration: '4.3s' },
  { x: '25px', y: '-48px', fx: '10px', fy: '-60px', size: '4px', delay: '-1.6s', duration: '3.6s' },
];

const PrayerCreationVisual = ({ phase }) => {
  const gathering = phase === 'gathering';
  return (
    <div
      className={`beloved-prayer-creation${gathering ? ' is-gathering' : ''}`}
      role="status"
      aria-live="polite"
      aria-label="Prayer being prepared"
    >
      <div className="beloved-prayer-sky" aria-hidden="true">
        <div className="beloved-prayer-halo" />
        {PRAYER_LIGHT_MOTES.map((mote, index) => (
          <span
            className="beloved-prayer-mote"
            key={`${mote.x}-${mote.y}`}
            style={{
              '--mote-x': mote.x,
              '--mote-y': mote.y,
              '--mote-float-x': mote.fx,
              '--mote-float-y': mote.fy,
              '--mote-delay': mote.delay,
              '--mote-duration': mote.duration,
              width: mote.size,
              height: mote.size,
              opacity: 0.45 + (index % 3) * 0.15,
            }}
          />
        ))}
        <div className="beloved-prayer-heart">
          <Icon name="heart" size={52} strokeWidth={1.45} />
        </div>
      </div>
      <p className="beloved-prayer-preparing-title">
        {gathering ? 'Your prayer is ready\u2026' : 'Preparing your prayer\u2026'}
      </p>
      <p className="beloved-prayer-preparing-note">Gathering words of comfort and hope.</p>
    </div>
  );
};

// A large primary (gold) button.
const primaryButtonStyle = {
  width: '100%',
  minHeight: '60px',
  padding: '18px 24px',
  borderRadius: '16px',
  border: 'none',
  background: `linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.goldLight} 100%)`,
  color: COLORS.bg,
  // A getter so the size honours the current text-size preference each time the
  // style is spread during a render (see scaleFont / activeTextScale).
  get fontSize() {
    return scaleFont(19);
  },
  fontWeight: 700,
  fontFamily: SERIF,
  cursor: 'pointer',
  boxShadow: '0 4px 20px rgba(212,175,55,0.35)',
};

// A large secondary (outline) button.
const secondaryButtonStyle = {
  width: '100%',
  minHeight: '56px',
  padding: '16px 24px',
  borderRadius: '16px',
  border: `1px solid ${COLORS.gold}`,
  background: 'rgba(212,175,55,0.08)',
  color: COLORS.gold,
  get fontSize() {
    return scaleFont(18);
  },
  fontWeight: 600,
  fontFamily: SERIF,
  cursor: 'pointer',
};

// A large list row (used for prayers and saved items).
const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  minHeight: '64px',
  padding: '18px',
  marginBottom: '12px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  borderRadius: '16px',
  background: COLORS.bgCard,
  border: `1px solid ${COLORS.border}`,
  cursor: 'pointer',
  textAlign: 'left',
  color: COLORS.text,
  fontFamily: SERIF,
};

// ============================================
// MAIN APP
// ============================================
export default function BelovedApp() {
  const [showWelcome, setShowWelcome] = useState(shouldShowWelcomeMoment);
  const [welcomeImage, setWelcomeImage] = useState(null);
  const [activeTab, setActiveTab] = useState('today');

  // The full-screen prayer reader (null when closed).
  const [activePrayer, setActivePrayer] = useState(null);

  // The "Help Me Pray" journey.
  const [helpStep, setHelpStep] = useState(null); // null | 1 | 2 | 3 | 'signin' | 'paywall'
  const [helpTheme, setHelpTheme] = useState(null);
  const [helpText, setHelpText] = useState('');
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpPrayer, setHelpPrayer] = useState(null); // { id, title, meta, body, note? }
  const [prayerVisualPhase, setPrayerVisualPhase] = useState('forming');
  const prayerRevealTimer = useRef(null);

  // The Beloved Plus paywall (shown only when the free allowance is used up).
  const [paywallPlan, setPaywallPlan] = useState(PRODUCT_ANNUAL); // which price is chosen
  const [paywallBusy, setPaywallBusy] = useState(false); // a purchase/restore is running
  const [paywallMessage, setPaywallMessage] = useState(''); // gentle status line
  const [plusPlans, setPlusPlans] = useState(FALLBACK_PLUS_PLANS);
  const [paywallContext, setPaywallContext] = useState('help'); // help | settings
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [purchaseRedemptionUrl, setPurchaseRedemptionUrl] = useState('');
  // Paid-activation resilience: once checkout gives payment evidence, we only
  // ever confirm or reassure — the generic failure line is unreachable.
  const [activationState, setActivationState] = useState(''); // '' | 'activating' | 'stalled'
  const [showPlusBlessing, setShowPlusBlessing] = useState(false);
  const activationRun = useRef(0);
  // The once-ever Beloved Plus moment after a free user's first personal prayer.
  const [showPlusMoment, setShowPlusMoment] = useState(false);

  // A free email account lets the same RevenueCat customer follow a person
  // between the website and the future iPhone app. Prayer content stays local.
  const [account, setAccount] = useState(null);
  const [accountReady, setAccountReady] = useState(() => !canUseAccounts());
  const [accountLinked, setAccountLinked] = useState(false);
  const [accountEmail, setAccountEmail] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [accountCodeSent, setAccountCodeSent] = useState(false);
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountMessage, setAccountMessage] = useState('');
  // Which free allowance applies to personal prayers. When account sign-in is
  // switched off or Supabase is unconfigured nobody can be asked to sign in, so
  // everyone keeps the ordinary monthly allowance rather than meeting a door
  // with no handle.
  const hasPrayerAccount = useMemo(
    () => !canUseAccounts() || Boolean(account),
    [account]
  );
  const [tier, setTier] = useState(() => getTier());
  const tierRef = useRef(tier);
  const syncTierFromStorage = useCallback(() => {
    const nextTier = getTier();
    if (tierRef.current === nextTier) return;
    tierRef.current = nextTier;
    setTier(nextTier);
  }, []);

  // Saved content. The brief notice confirms the action, then clears so the
  // save control fully disappears instead of turning into a permanent button.
  const [savedPrayers, setSavedPrayers] = useState([]);
  const [savedVerses, setSavedVerses] = useState([]);
  const [saveNotice, setSaveNotice] = useState(null);
  const saveNoticeTimer = useRef(null);
  const [shareNotice, setShareNotice] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [pendingPrayerShare, setPendingPrayerShare] = useState(null);
  const hydrated = useRef(false);

  // The one-time "gentle daily reminder" opt-in card (native app only).
  const [showReminderCard, setShowReminderCard] = useState(false);

  // Settings: whether daily reminders are currently on (native only), and a
  // gentle status line for the "Restore a purchase" action.
  const [remindersOn, setRemindersOn] = useState(false);
  const [reminderTimes, setReminderTimes] = useState(() => getReminderTimes());
  const [reminderMessage, setReminderMessage] = useState('');
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [backupMessage, setBackupMessage] = useState('');
  const [backupBusy, setBackupBusy] = useState(false);
  const [clearPrivateConfirmation, setClearPrivateConfirmation] = useState(false);
  const [devicePrivacyMessage, setDevicePrivacyMessage] = useState('');
  const backupInputRef = useRef(null);

  // Speech: whether a prayer is being read aloud, and whether we're listening
  // for the person's voice on the "Tell Beloved" step. Both features are hidden
  // entirely when the device can't do them (see canSpeak/canListen).
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechPreparing, setSpeechPreparing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechInputMessage, setSpeechInputMessage] = useState('');
  const [speechInputHasError, setSpeechInputHasError] = useState(false);

  // The Amen moment — a reverent benediction that plays over the top when any
  // Amen button is tapped, then runs the normal close. `amenLine` is the blessing
  // shown (a string) while the overlay is up, or null when hidden. Refs hold the
  // close handler to run when it finishes, the single auto-dismiss timer, an
  // idempotency guard (so the close runs exactly once even on rapid taps), and
  // the last benediction index so the same line never shows twice running.
  const [amenLine, setAmenLine] = useState(null);
  const amenCloseRef = useRef(null);
  const amenTimer = useRef(null);
  const amenDone = useRef(false);
  const lastBenediction = useRef(-1);

  // Continuity — the intentions Beloved remembers for this person, ONLY on this
  // device. `helpLabel` is the friendly label of the chosen theme (e.g. "Someone
  // I love"), stored with an intention so the Today card never needs a lookup.
  // `helpSourceIntentionId` is set when the Help journey was opened from a stored
  // intention, so a fresh prayer marks that intention as prayed. `intentionSaved`
  // confirms the quiet "keep this close" line. `resolvedNote` is a brief warm
  // acknowledgement shown after letting an intention go.
  const [intentions, setIntentions] = useState([]);
  const [helpLabel, setHelpLabel] = useState(null);
  const [helpSourceIntentionId, setHelpSourceIntentionId] = useState(null);
  const [intentionSaved, setIntentionSaved] = useState(false);
  const [resolvedNote, setResolvedNote] = useState('');
  const resolveTimer = useRef(null);

  // Text size preference ('comfortable' | 'large' | 'extraLarge'), read at mount.
  const [textSize, setTextSize] = useState('comfortable');

  // Set the render-wide font scale before any JSX below is built. This is the
  // single source the scaleFont helper reads, so every surface scales together.
  activeTextScale = TEXT_SIZE_SCALE[textSize] || 1;

  const dailyVerse = getDailyVerse();
  const dailyArtwork = getArtworkOfDay();

  // Load saved content on first mount.
  useEffect(() => {
    setWelcomeImage(getArtworkOfDay());
    try {
      const p = localStorage.getItem('beloved_saved_prayers');
      if (p) setSavedPrayers(JSON.parse(p));
      const v = localStorage.getItem('beloved_saved_verses');
      if (v) setSavedVerses(JSON.parse(v));
    } catch (e) {
      // A first-time or blocked storage is fine; start empty.
    }
    // Continuity and accessibility preferences (both device-local).
    setIntentions(getIntentions());
    try {
      const size = localStorage.getItem('beloved_text_size');
      if (size === 'extraLarge' || size === 'large' || size === 'comfortable') setTextSize(size);
    } catch (e) {
      // No stored preference (or blocked storage): comfortable is the default.
    }
    hydrated.current = true;
    // Decide once, on the native app only, whether to offer daily reminders.
    // On the web this is always false, so the card and prompt never appear.
    setShowReminderCard(shouldShowReminderPrompt());
    // Reflect the current reminder state in the Settings toggle (native only).
    setRemindersOn(remindersEnabled());
    setReminderTimes(getReminderTimes());
  }, []);

  // Restore a Supabase session and keep RevenueCat on that same non-personal
  // account UUID. `identifyPurchasesUser` also aliases a previous anonymous
  // purchase the first time someone signs in.
  useEffect(() => {
    if (!canUseAccounts()) {
      let active = true;
      refreshTier().then(() => {
        if (active) syncTierFromStorage();
      });
      return () => {
        active = false;
      };
    }
    let active = true;
    let liveAuthEventHasReported = false;
    let accountOperation = Promise.resolve();

    const applyAccount = async (user) => {
      if (!active) return;
      setAccount(user || null);
      setAccountLinked(false);
      if (!user || !user.id) {
        // Supabase and RevenueCat keep separate sessions. When the auth session
        // expires or is revoked, detach any custom RevenueCat account too. An
        // existing anonymous purchase is deliberately preserved by the helper.
        setAccountReady(false);
        const detached = await resetPurchasesUser();
        if (active) {
          syncTierFromStorage();
          setAccountReady(true);
          if (detached.status === 'error') {
            setAccountMessage('Your account signed out. For safety, reload Beloved before purchasing or restoring access.');
          }
        }
        return;
      }
      setAccountReady(true);
      if (user.email) setAccountEmail(user.email);

      const result = await identifyPurchasesUser(user.id);
      if (!active) return;
      syncTierFromStorage();
      if (result.status === 'success') {
        setAccountLinked(true);
        setAccountMessage('Your Beloved Plus purchase is protected by your account.');
      } else if ((result.status === 'none' || result.status === 'error') && getTier() === 'plus') {
        setAccountMessage('Plus is still active on this device. We could not protect it just now; please try again.');
      }
      const isEmailLinkReturn =
        new URLSearchParams(window.location.search).get('account') === 'confirmed';
      const returnTo = isEmailLinkReturn ? consumeAccountReturn() : '';
      if (returnTo === 'plus') {
        setPaywallContext('settings');
        setPaywallMessage("You're signed in. Choose a plan when you're ready.");
        setHelpStep('paywall');
      } else if (returnTo === 'protect') {
        setShowWelcome(false);
        setActiveTab('settings');
        setAccountMessage('You are signed in. We are checking that your Plus purchase is protected.');
      } else if (returnTo === 'settings') {
        setActiveTab('settings');
        setAccountMessage("You're signed in.");
      }
    };

    const queueAccount = (user) => {
      accountOperation = accountOperation.then(() => applyAccount(user));
    };

    getCurrentAccount().then((user) => {
      if (!liveAuthEventHasReported) queueAccount(user);
    });
    const stopWatching = watchAccount((user, event) => {
      // INITIAL_SESSION is recovered from local storage and can still describe
      // a revoked session. Let the server-validated getUser result own startup.
      if (event === 'INITIAL_SESSION') return;
      liveAuthEventHasReported = true;
      queueAccount(user);
    });
    return () => {
      active = false;
      stopWatching();
    };
  }, [syncTierFromStorage]);

  // Store prices are localized by Apple/RevenueCat. The static UK launch
  // figures remain visible on the web and while a native store is unavailable.
  useEffect(() => {
    if (helpStep !== 'paywall') return undefined;
    let active = true;
    getPlusPlanPricing().then((plans) => {
      if (active && plans !== FALLBACK_PLUS_PLANS) setPlusPlans(plans);
    });
    return () => {
      active = false;
    };
  }, [helpStep]);

  // Persist saved content whenever it changes (after the first load).
  useEffect(() => {
    if (!hydrated.current) return;
    try {
      localStorage.setItem('beloved_saved_prayers', JSON.stringify(savedPrayers));
    } catch (e) {
      // Ignore storage failures silently.
    }
  }, [savedPrayers]);

  useEffect(() => {
    if (!hydrated.current) return;
    try {
      localStorage.setItem('beloved_saved_verses', JSON.stringify(savedVerses));
    } catch (e) {
      // Ignore storage failures silently.
    }
  }, [savedVerses]);

  // Persist the text-size preference whenever it changes (after first load).
  useEffect(() => {
    if (!hydrated.current) return;
    try {
      localStorage.setItem('beloved_text_size', textSize);
    } catch (e) {
      // A blocked storage just means the choice won't be remembered next time.
    }
  }, [textSize]);

  // ---- Read aloud ----
  // Toggle reading the given title + body. A second tap stops. Reading always
  // resets its button to "Read aloud" when it finishes on its own (onEnd).
  const toggleReadAloud = useCallback(
    (title, body) => {
      if (isSpeaking) {
        stopSpeaking();
        setIsSpeaking(false);
        setSpeechPreparing(false);
        return;
      }
      const text = [title, body].filter(Boolean).join('. ');
      setIsSpeaking(true);
      setSpeechPreparing(usesEnhancedVoice());
      speak(
        text,
        () => {
          setIsSpeaking(false);
          setSpeechPreparing(false);
        },
        () => setSpeechPreparing(false)
      );
    },
    [isSpeaking]
  );

  // Stop reading whenever the visible prayer changes or both prayer surfaces
  // close — this is what makes reading stop automatically on Back/Amen/navigate.
  useEffect(() => {
    stopSpeaking();
    setIsSpeaking(false);
    setSpeechPreparing(false);
    setShareNotice('');
    setPendingPrayerShare(null);
  }, [activePrayer, helpStep, helpPrayer]);

  // Belt-and-braces: stop all speech (and clear the farewell timer) if the app
  // itself unmounts.
  useEffect(() => () => {
    stopSpeaking();
    stopListening();
    if (resolveTimer.current) clearTimeout(resolveTimer.current);
    if (prayerRevealTimer.current) clearTimeout(prayerRevealTimer.current);
    if (saveNoticeTimer.current) clearTimeout(saveNoticeTimer.current);
  }, []);

  // ---- Voice input (Help step 2) ----
  // Toggle listening; each final phrase is appended to whatever is already in
  // the textarea, separated by a space, and stays fully editable.
  const toggleListening = useCallback(() => {
    if (isListening) {
      setSpeechInputMessage('Finishing your words…');
      stopListening();
      setIsListening(false);
      return;
    }

    let receivedWords = false;
    let recognitionFailed = false;
    const speechErrorMessage = (code) => {
      const messages = {
        'not-allowed': 'Microphone access is blocked. Allow it in your browser or iPhone settings, then try again.',
        'service-not-allowed': 'Speech recognition is blocked on this device. Allow microphone and speech recognition access, then try again.',
        'audio-capture': 'Beloved could not find a working microphone. Check your microphone and try again.',
        network: 'Speech recognition needs an internet connection. Check your connection and try again.',
        'no-speech': 'Beloved did not hear any words. Tap Speak instead and try again.',
        unsupported: 'Voice input is not supported here. You can type, or use the microphone key on your phone keyboard.',
        unavailable: 'Voice input could not start. Check microphone access, then try again.',
      };
      if (code === 'aborted') return;
      recognitionFailed = true;
      setSpeechInputHasError(true);
      setSpeechInputMessage(messages[code] || 'Voice input stopped unexpectedly. Please try again.');
    };

    setSpeechInputHasError(false);
    setSpeechInputMessage('Starting the microphone…');
    setIsListening(true);
    const started = startListening(
      (transcript) => {
        receivedWords = true;
        setHelpText((prev) => (prev && prev.trim() ? `${prev.trim()} ${transcript}` : transcript));
        setSpeechInputHasError(false);
        setSpeechInputMessage('Your spoken words were added above.');
      },
      () => {
        setIsListening(false);
        if (!receivedWords && !recognitionFailed) {
          setSpeechInputHasError(false);
          setSpeechInputMessage('No words were added. Tap Speak instead and try again.');
        }
      },
      speechErrorMessage,
      () => {
        setSpeechInputHasError(false);
        setSpeechInputMessage('Listening… speak naturally, then tap to finish.');
      }
    );
    if (!started) setIsListening(false);
  }, [isListening]);

  // Stop listening whenever we leave the "Tell Beloved" step.
  useEffect(() => {
    if (helpStep !== 2) {
      stopListening();
      setIsListening(false);
      setSpeechInputMessage('');
      setSpeechInputHasError(false);
    }
  }, [helpStep]);

  const handleWelcomeComplete = useCallback(() => setShowWelcome(false), []);

  // ---- Daily reminders opt-in card ----
  // "Turn on" is the only path that asks the operating system for permission.
  const handleTurnOnReminders = useCallback(async () => {
    setShowReminderCard(false);
    const on = await enableDailyReminders();
    dismissReminderPrompt(on ? 'on' : 'later');
    setRemindersOn(on);
  }, []);

  const handleDismissReminders = useCallback(() => {
    setShowReminderCard(false);
    dismissReminderPrompt('later');
  }, []);

  // ---- Settings: daily-reminder toggle ----
  // Turning on runs the same permission-then-schedule flow as the opt-in card;
  // turning off cancels both scheduled notifications. The toggle only ever shows
  // on the native app, so this is never reached on the web.
  const handleToggleReminders = useCallback(async () => {
    if (settingsBusy) return;
    setSettingsBusy(true);
    if (remindersOn) {
      await disableDailyReminders();
      setRemindersOn(false);
      setReminderMessage('Daily reminders are off.');
    } else {
      const on = await enableDailyReminders();
      if (on) {
        dismissReminderPrompt('on');
        setRemindersOn(true);
        setReminderMessage('Daily reminders are on.');
      } else {
        setReminderMessage('Reminders are still off. You can allow them in iPhone Settings.');
      }
    }
    setSettingsBusy(false);
  }, [remindersOn, settingsBusy]);

  const handleReminderTimeChange = useCallback(
    async (period, value) => {
      const next = saveReminderTimes({ ...reminderTimes, [period]: value });
      setReminderTimes(next);
      if (!remindersOn) {
        setReminderMessage('Times saved. They will be used when reminders are turned on.');
        return;
      }
      setReminderMessage('Updating reminder times…');
      const updated = await rescheduleDailyReminders(next);
      setReminderMessage(
        updated
          ? 'Reminder times updated.'
          : 'The times were saved, but reminders could not be updated just now.'
      );
    },
    [reminderTimes, remindersOn]
  );

  const handleEmailSignIn = useCallback(
    async (event, returnTo = 'settings') => {
      event.preventDefault();
      if (accountBusy) return;
      setAccountBusy(true);
      setAccountMessage('');
      const result = await emailSignInCode(accountEmail, returnTo);
      setAccountBusy(false);
      if (result.status === 'sent') {
        setAccountEmail(result.email);
        setAccountCode('');
        setAccountCodeSent(true);
        setAccountMessage(
          `We sent a secure sign-in link to ${result.email}. Open it to finish signing in. If the email also shows a six-digit code, you can enter it here instead.`
        );
      } else if (result.status === 'invalid') {
        setAccountMessage('Please enter a complete email address.');
      } else if (result.status === 'unavailable') {
        setAccountMessage("Account sign-in isn't available just now.");
      } else {
        setAccountMessage("We couldn't send the link just now. Please try again.");
      }
    },
    [accountBusy, accountEmail]
  );

  const handleVerifyAccountCode = useCallback(
    async (event) => {
      event.preventDefault();
      if (accountBusy) return;
      setAccountBusy(true);
      setAccountMessage('Checking your code...');
      const result = await verifyEmailSignInCode(accountEmail, accountCode);
      setAccountBusy(false);
      if (result.status === 'success') {
        // The same-tab code completed the return, so a later page load must not
        // replay the magic-link fallback destination kept in local storage.
        consumeAccountReturn();
        setAccount(result.user);
        setAccountCode('');
        setAccountCodeSent(false);
        setAccountMessage(
          getTier() === 'plus'
            ? 'Signed in. We are protecting your Plus purchase now...'
            : "You're signed in."
        );
      } else if (result.status === 'invalid') {
        setAccountMessage('That code is incorrect or has expired. Please check it or request a new code.');
      } else if (result.status === 'unavailable') {
        setAccountMessage("Account sign-in isn't available just now.");
      } else {
        setAccountMessage("We couldn't check that code just now. Your purchase is still active on this device.");
      }
    },
    [accountBusy, accountCode, accountEmail]
  );

  const retryAccountProtection = useCallback(async () => {
    if (accountBusy || !account || !account.id) return;
    setAccountBusy(true);
    setAccountMessage('Checking your purchase...');
    const result = await identifyPurchasesUser(account.id);
    syncTierFromStorage();
    setAccountBusy(false);
    if (result.status === 'success') {
      setAccountLinked(true);
      setAccountMessage('Your Beloved Plus purchase is protected by your account.');
    } else {
      setAccountLinked(false);
      setAccountMessage('Plus is still active on this device. We could not protect it just now; please try again.');
    }
  }, [account, accountBusy, syncTierFromStorage]);

  const handleAccountSignOut = useCallback(async () => {
    if (accountBusy) return;
    setAccountBusy(true);
    setAccountMessage('');
    // Detach the purchase customer first. If that safety step fails, keep the
    // account signed in so a shared browser never appears signed out while it
    // can still use the previous person's paid entitlement.
    const purchaseReset = await resetPurchasesUser();
    syncTierFromStorage();
    if (purchaseReset.status === 'error') {
      setAccountMessage("We couldn't safely sign out just now. Please check your connection and try again.");
      setAccountBusy(false);
      return;
    }
    const result = await signOutAccount();
    if (result.status === 'success') {
      setAccount(null);
      setAccountLinked(false);
      setAccountEmail('');
      setAccountCode('');
      setAccountCodeSent(false);
      setAccountMessage(
        savedPrayers.length > 0 || savedVerses.length > 0 || intentions.length > 0
          ? "You're signed out on this device. Saved prayers and prayer intentions remain here until you clear them."
          : "You're signed out on this device."
      );
    } else {
      // Authentication stayed active, so restore the purchase link we just
      // detached and leave the visible account state unchanged.
      if (account && account.id && purchaseReset.status === 'success') {
        await identifyPurchasesUser(account.id);
        syncTierFromStorage();
      }
      setAccountMessage("We couldn't sign out just now. Please try again.");
    }
    setAccountBusy(false);
  }, [account, accountBusy, intentions.length, savedPrayers.length, savedVerses.length, syncTierFromStorage]);

  const openPlusFromSettings = () => {
    resetPaywall();
    setPaywallContext('settings');
    setHelpStep('paywall');
  };

  const closeSettingsPaywall = () => {
    resetPaywall();
    setHelpStep(null);
    setPaywallContext('help');
    setActiveTab('settings');
  };

  // ---- Settings: restore a Beloved Plus purchase ----
  // Uses the same gentle messages as the paywall's restore handler.
  const handleRestoreInSettings = useCallback(async () => {
    if (settingsBusy) return;
    setSettingsBusy(true);
    setSettingsMessage('');
    const result = await restorePlus();
    syncTierFromStorage();
    setSettingsBusy(false);
    if (result.status === 'success') {
      setSettingsMessage('Beloved Plus is active — thank you.');
    } else if (result.status === 'unavailable') {
      setSettingsMessage('Beloved Plus purchases aren\u2019t available just now.');
    } else if (result.status === 'none') {
      setSettingsMessage('We couldn’t find a previous purchase to restore.');
    } else {
      setSettingsMessage('We couldn’t restore just now. Please try again in a moment.');
    }
  }, [settingsBusy, syncTierFromStorage]);

  const handleManageSubscription = useCallback(async () => {
    if (settingsBusy) return;
    setSettingsBusy(true);
    setSettingsMessage('Opening subscription management...');
    const result = await getSubscriptionManagementUrl();
    setSettingsBusy(false);
    if (result.status === 'success') {
      setSettingsMessage('Taking you to subscription management...');
      // A same-tab navigation remains reliable after the async RevenueCat
      // lookup; opening a new tab here is commonly blocked by Safari.
      window.location.assign(result.url);
    } else if (result.status === 'none') {
      setSettingsMessage('We could not find a subscription-management link. Try Restore a purchase, then try again.');
    } else {
      setSettingsMessage('Subscription management is not available just now. Please try again in a moment.');
    }
  }, [settingsBusy]);

  // The clean route is a real static legal page in production.
  const openPrivacyPolicy = useCallback(() => {
    window.open(`${PUBLIC_SITE_ORIGIN}/privacy`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleExportBackup = useCallback(async () => {
    if (backupBusy) return;
    setBackupBusy(true);
    setBackupMessage('');
    try {
      await exportBackupFile();
      setBackupMessage('Your private backup is ready.');
    } catch (error) {
      if (!error || error.name !== 'AbortError') {
        setBackupMessage('We couldn’t create the backup just now. Please try again.');
      }
    } finally {
      setBackupBusy(false);
    }
  }, [backupBusy]);

  const handleImportBackup = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setBackupBusy(true);
    setBackupMessage('');
    try {
      const restored = await restoreBackupFile(file);
      setSavedPrayers(restored.savedPrayers);
      setSavedVerses(restored.savedVerses);
      setIntentions(restored.intentions);
      setTextSize(restored.textSize);
      setReminderTimes(restored.reminderTimes);
      const remindersUpdated = remindersOn
        ? await rescheduleDailyReminders(restored.reminderTimes)
        : true;
      setBackupMessage(
        remindersUpdated
          ? 'Your saved prayers, verses, and preferences have been restored.'
          : 'Your content was restored. Open Daily reminders to check the notification times.'
      );
    } catch (error) {
      setBackupMessage(error && error.message ? error.message : 'That backup could not be restored.');
    } finally {
      setBackupBusy(false);
    }
  }, [remindersOn]);

  const handleClearPrivateContent = useCallback(() => {
    if (!clearPrivateContent()) {
      setDevicePrivacyMessage('We could not clear this device just now. Please try again.');
      return;
    }
    setSavedPrayers([]);
    setSavedVerses([]);
    setIntentions([]);
    setPendingPrayerShare(null);
    setClearPrivateConfirmation(false);
    setDevicePrivacyMessage('Private content has been cleared from this device.');
  }, []);

  // ---- Saving helpers ----
  const showSaveNotice = (key) => {
    if (saveNoticeTimer.current) clearTimeout(saveNoticeTimer.current);
    setSaveNotice(key);
    saveNoticeTimer.current = setTimeout(() => {
      setSaveNotice(null);
      saveNoticeTimer.current = null;
    }, 1900);
  };

  const isVerseSaved = (id) => savedVerses.some((v) => v.id === id);
  const saveVerse = (verse) => {
    setSavedVerses((prev) =>
      prev.some((v) => v.id === verse.id)
        ? prev
        : [{ id: verse.id, ref: verse.ref, text: verse.text, reflection: verse.reflection }, ...prev]
    );
    showSaveNotice(`verse:${verse.id}`);
  };
  const removeVerse = (id) => setSavedVerses((prev) => prev.filter((v) => v.id !== id));

  const isPrayerSaved = (id) => savedPrayers.some((p) => p.id === id);
  const savePrayer = (prayer) => {
    setSavedPrayers((prev) =>
      prev.some((p) => p.id === prayer.id)
        ? prev
        : [{ id: prayer.id, title: prayer.title, meta: prayer.meta, body: prayer.body, personal: Boolean(prayer.personal) }, ...prev]
    );
    showSaveNotice(`prayer:${prayer.id}`);
  };
  const removePrayer = (id) => setSavedPrayers((prev) => prev.filter((p) => p.id !== id));

  // ---- Privacy-safe sharing ----
  // Prayer/verse cards are drawn locally; nothing is uploaded to make them.
  // Personal prayers always pause for an explicit review-and-share decision.
  const applyShareResult = (result) => {
    if (!result || result.status === 'cancelled') {
      setShareNotice('');
    } else if (result.status === 'shared') {
      setShareNotice('Shared from Beloved.');
    } else if (result.status === 'copied') {
      setShareNotice('Copied — ready to paste into a message.');
    } else {
      setShareNotice('Sharing is not available on this device just now.');
    }
  };

  const handleShareVerse = async () => {
    if (shareBusy) return;
    setShareBusy(true);
    setShareNotice('');
    try {
      applyShareResult(await shareVerse(dailyVerse));
    } finally {
      setShareBusy(false);
    }
  };

  const handleSharePrayer = async (prayer, confirmed = false) => {
    if (!prayer || shareBusy) return;
    if (isPersonalPrayer(prayer) && !confirmed) {
      setShareNotice('');
      setPendingPrayerShare(prayer);
      return;
    }
    setShareBusy(true);
    setShareNotice('');
    try {
      const result = await sharePrayer(prayer, { confirmed });
      if (result && result.status === 'confirmation-required') {
        setPendingPrayerShare(prayer);
      } else {
        setPendingPrayerShare(null);
        applyShareResult(result);
      }
    } finally {
      setShareBusy(false);
    }
  };

  const renderShareFeedback = () =>
    shareNotice ? (
      <p
        role="status"
        aria-live="polite"
        style={{ fontSize: scaleFont(16), color: COLORS.gold, textAlign: 'center', lineHeight: 1.55, margin: '14px 4px 0' }}
      >
        {shareNotice}
      </p>
    ) : null;

  const renderPrayerShareConfirmation = (prayer) => {
    if (!pendingPrayerShare || !prayer || pendingPrayerShare.id !== prayer.id) return null;
    return (
      <section
        aria-label="Confirm sharing this personal prayer"
        style={{ marginTop: '14px', padding: '18px', borderRadius: '14px', border: `1px solid ${COLORS.gold}`, background: 'rgba(212,175,55,0.08)' }}
      >
        <p style={{ fontSize: scaleFont(17), color: COLORS.cream, lineHeight: 1.55, margin: '0 0 14px', textAlign: 'center' }}>
          This prayer may contain something personal. Review it above before sharing.
        </p>
        <button
          onClick={() => handleSharePrayer(prayer, true)}
          disabled={shareBusy}
          style={{ ...primaryButtonStyle, opacity: shareBusy ? 0.65 : 1 }}
        >
          {shareBusy ? 'Preparing share…' : 'Share this prayer'}
        </button>
        <button
          onClick={() => setPendingPrayerShare(null)}
          disabled={shareBusy}
          style={{ ...secondaryButtonStyle, marginTop: '10px', border: 'none', background: 'none', color: COLORS.secondary }}
        >
          Keep it private
        </button>
      </section>
    );
  };

  // ---- Opening prayers in the reader ----
  const openThemedPrayer = (themeId, index = 0) => {
    const theme = THEMED_PRAYERS[themeId];
    const prayer = theme.prayers[index];
    setActivePrayer({
      id: `${themeId}-${index}`,
      title: prayer.title,
      meta: theme.label,
      body: prayer.body,
      themeId,
      index,
      canSave: true,
    });
  };

  const openSetPrayer = (prayer) => {
    setActivePrayer({
      id: prayer.id,
      title: prayer.title,
      meta: prayer.subtitle || prayer.reference || '',
      body: prayer.prayer,
      canSave: true,
    });
  };

  const openSavedPrayer = (prayer) => {
    setActivePrayer({
      id: prayer.id,
      title: prayer.title,
      meta: prayer.meta,
      body: prayer.body,
      personal: Boolean(prayer.personal) || isPersonalPrayer(prayer),
      canSave: true,
    });
  };

  const openSavedVerse = (verse) => {
    setActivePrayer({
      id: verse.id,
      title: verse.ref,
      meta: 'Scripture',
      body: `“${verse.text}”`,
      reflection: verse.reflection,
      canSave: false,
    });
  };

  const closeReader = () => setActivePrayer(null);
  const returnToTodayFromReader = () => {
    stopSpeaking();
    setIsSpeaking(false);
    setActivePrayer(null);
    setActiveTab('today');
  };

  // ---- Help Me Pray journey ----
  const resetPaywall = () => {
    setPaywallPlan(PRODUCT_ANNUAL);
    setPaywallBusy(false);
    setPaywallMessage('');
    setPurchaseComplete(false);
    setPurchaseRedemptionUrl('');
    setAccountCode('');
    setAccountCodeSent(false);
    setActivationState('');
    activationRun.current += 1;
  };

  const startHelp = () => {
    if (prayerRevealTimer.current) {
      clearTimeout(prayerRevealTimer.current);
      prayerRevealTimer.current = null;
    }
    setPrayerVisualPhase('forming');
    setHelpTheme(null);
    setHelpText('');
    setHelpLabel(null);
    setHelpSourceIntentionId(null);
    setHelpPrayer(null);
    setHelpLoading(false);
    setIntentionSaved(false);
    setPaywallContext('help');
    resetPaywall();
    setHelpStep(1);
  };

  const closeHelp = () => {
    if (prayerRevealTimer.current) {
      clearTimeout(prayerRevealTimer.current);
      prayerRevealTimer.current = null;
    }
    setPrayerVisualPhase('forming');
    setHelpStep(null);
    setHelpTheme(null);
    setHelpText('');
    setHelpLabel(null);
    setHelpSourceIntentionId(null);
    setHelpPrayer(null);
    setHelpLoading(false);
    setIntentionSaved(false);
    setPaywallContext('help');
    resetPaywall();
    setActiveTab('today');
  };

  // The Amen close for a completed personal prayer. The first such journey on
  // a free device earns one — and only one — gentle Beloved Plus moment.
  const closeHelpAfterAmen = () => {
    const hadPersonalPrayer = Boolean(helpPrayer);
    closeHelp();
    if (
      hadPersonalPrayer &&
      getTier() !== 'plus' &&
      !localStorage.getItem('beloved_plus_moment_shown')
    ) {
      localStorage.setItem('beloved_plus_moment_shown', 'true');
      setShowPlusMoment(true);
    }
  };

  // ---- The Amen moment ----
  // Finish the benediction and hand off to the real close handler. Idempotent:
  // the first call (a tap-to-skip or the auto timer) wins, and every later call
  // is a safe no-op — so a rapid double-tap can never close twice or leave the
  // app stranded. `closeFn` is Beloved's own closeReader/closeHelp, kept as the
  // single exit path to Today. This never throws, so the close ALWAYS runs.
  const finishAmen = useCallback(() => {
    if (amenDone.current) return;
    amenDone.current = true;
    if (amenTimer.current) {
      clearTimeout(amenTimer.current);
      amenTimer.current = null;
    }
    const closeFn = amenCloseRef.current;
    amenCloseRef.current = null;
    setAmenLine(null);
    if (typeof closeFn === 'function') closeFn();
  }, []);

  // Begin the Amen moment: stop any reading at once (kept from the old close
  // behaviour), pick a blessing that isn't the one just shown, remember which
  // close to run, and raise the overlay. A single timer auto-dismisses it; if
  // that timer never fired, a tap anywhere still calls finishAmen.
  const beginAmen = useCallback((closeFn) => {
    stopSpeaking();
    setIsSpeaking(false);
    const { index, line } = pickBenediction(lastBenediction.current);
    lastBenediction.current = index;
    amenCloseRef.current = typeof closeFn === 'function' ? closeFn : null;
    amenDone.current = false;
    if (amenTimer.current) {
      clearTimeout(amenTimer.current);
      amenTimer.current = null;
    }
    setAmenLine(line || BENEDICTIONS[0]);
  }, []);

  // Arm the single auto-dismiss timer whenever the overlay goes up. ~2.2s for
  // the full moment, ~1.2s under reduced motion. Cleared on skip or unmount.
  useEffect(() => {
    if (amenLine == null) return undefined;
    const total = prefersReducedMotion() ? 1200 : 2200;
    amenTimer.current = setTimeout(finishAmen, total);
    return () => {
      if (amenTimer.current) {
        clearTimeout(amenTimer.current);
        amenTimer.current = null;
      }
    };
  }, [amenLine, finishAmen]);

  const chooseHelpOption = (option) => {
    setHelpTheme(option.theme);
    setHelpLabel(option.label);
    // A brand-new choice is not (yet) a remembered intention.
    setHelpSourceIntentionId(null);
    setIntentionSaved(false);
    setHelpStep(2);
  };

  // "Pray for something else" from a finished prayer: begin a fresh choice,
  // forgetting which intention (if any) this prayer grew from.
  const restartHelpChoice = () => {
    setHelpText('');
    setHelpLabel(null);
    setHelpSourceIntentionId(null);
    setHelpPrayer(null);
    setIntentionSaved(false);
    setHelpStep(1);
  };

  // Write a prayer for an explicit theme + words, so it can be driven either by
  // the journey's own state (the normal path) or straight from a stored
  // intention (the Today card), without waiting for state to settle.
  const runPrayer = useCallback(async (theme, text, sourceIntentionId) => {
    if (prayerRevealTimer.current) {
      clearTimeout(prayerRevealTimer.current);
      prayerRevealTimer.current = null;
    }
    setPrayerVisualPhase('forming');
    setHelpStep(3);
    setHelpLoading(true);
    setIntentionSaved(false);
    const result = await createPrayer(theme, text);
    // Charge on success only: a free person's allowance is spent solely when a
    // genuinely personalised prayer is delivered (source === 'ai'). If the
    // service fell back to a hand-written local prayer, we do not take a use.
    // (For Plus users recordAiPrayerUse() is already a no-op, so this is harmless.)
    if (result.source === 'ai') {
      recordAiPrayerUse(hasPrayerAccount);
    }
    const label = THEMED_PRAYERS[theme] ? THEMED_PRAYERS[theme].label : 'A Prayer for You';
    const usedFallback = result.source !== 'ai';
    const fairUsePause = result.reason === 'fair_use';
    setHelpPrayer({
      id: `help-${Date.now()}`,
      title: 'A Prayer for You',
      meta: label,
      body: result.text,
      personal: true,
      source: result.source,
      retryable: usedFallback && !fairUsePause,
      note: usedFallback
        ? fairUsePause
          ? `Beloved has paused personal writing for a little while to keep it available for everyone. Here is a trusted prayer for ${label.toLowerCase()}; your allowance was not used.`
          : `We couldn’t personalise this just now, so here is a trusted prayer for ${label.toLowerCase()}. Your free allowance was not used.`
        : '',
    });
    setPrayerVisualPhase('gathering');
    const revealDelay = prefersReducedMotion() ? 160 : 520;
    prayerRevealTimer.current = setTimeout(() => {
      setHelpLoading(false);
      setPrayerVisualPhase('forming');
      prayerRevealTimer.current = null;
    }, revealDelay);
    // When this prayer grew from a remembered intention, note that it was just
    // prayed (its lastPrayedAt moves it to the front of the Today card).
    if (sourceIntentionId) {
      setIntentions(markPrayed(sourceIntentionId));
    }
  }, [hasPrayerAccount]);

  const generateHelpPrayer = useCallback(
    () => runPrayer(helpTheme, helpText, helpSourceIntentionId),
    [runPrayer, helpTheme, helpText, helpSourceIntentionId]
  );

  // The gate: Step 2's "Continue". A personal prayer is the ONLY thing gated.
  // One try-it prayer, then a free account opens 3 a month; once those are used
  // up, Continue leads to the paywall. The charge itself happens in
  // generateHelpPrayer, only on a personalised (source === 'ai') delivery — a
  // fallback prayer never costs a use.
  const continueFromDetails = useCallback(() => {
    if (canUseAiPrayer(hasPrayerAccount)) {
      generateHelpPrayer();
    } else if (!hasPrayerAccount) {
      setHelpStep('signin');
    } else {
      resetPaywall();
      setPaywallContext('help');
      setHelpStep('paywall');
    }
  }, [generateHelpPrayer, hasPrayerAccount]);

  // Signing in from the prayer gate should feel like the door simply opening:
  // the moment an account appears, carry straight on into the prayer they had
  // already written. The latch keeps a re-run of this effect from asking for
  // the same prayer twice.
  const signInResumeRef = useRef(false);
  useEffect(() => {
    if (helpStep !== 'signin') {
      signInResumeRef.current = false;
      return;
    }
    if (!hasPrayerAccount || signInResumeRef.current) return;
    signInResumeRef.current = true;
    if (canUseAiPrayer(hasPrayerAccount)) {
      generateHelpPrayer();
    } else {
      setPaywallContext('help');
      setHelpStep('paywall');
    }
  }, [helpStep, hasPrayerAccount, generateHelpPrayer]);

  // "Maybe later" on the paywall: the person still receives a real prayer — a
  // hand-written one for their theme — with one gentle note about Beloved Plus.
  const dismissPaywallWithPrayer = () => {
    const label = THEMED_PRAYERS[helpTheme] ? THEMED_PRAYERS[helpTheme].label : 'A Prayer for You';
    setHelpPrayer({
      id: `help-${Date.now()}`,
      title: 'A Prayer for You',
      meta: label,
      body: getFallbackPrayer(helpTheme),
      personal: true,
      note: 'Beloved Plus creates prayers written just for you.',
    });
    setHelpLoading(false);
    setIntentionSaved(false);
    resetPaywall();
    setHelpStep(3);
    // A hand-written prayer is still a prayer for a remembered intention.
    if (helpSourceIntentionId) {
      setIntentions(markPrayed(helpSourceIntentionId));
    }
  };

  // ---- Continuity: prayer intentions Beloved remembers (device-only) ----

  // Today's "Still on your heart" card taps straight into a fresh prayer for a
  // stored intention. The same gate as the normal journey applies: if the free
  // allowance is used up, the person meets the sign-in step or the paywall
  // exactly as usual.
  const continuePrayingIntention = useCallback(
    (intention) => {
      setHelpTheme(intention.theme);
      setHelpText(intention.words || '');
      setHelpLabel(intention.label);
      setHelpSourceIntentionId(intention.id);
      setHelpPrayer(null);
      setHelpLoading(false);
      setIntentionSaved(false);
      resetPaywall();
      if (canUseAiPrayer(hasPrayerAccount)) {
        runPrayer(intention.theme, intention.words || '', intention.id);
      } else if (!hasPrayerAccount) {
        setHelpStep('signin');
      } else {
        setHelpStep('paywall');
      }
    },
    [runPrayer, hasPrayerAccount]
  );

  // "Pray about this again tomorrow" on the finished prayer: remember (or
  // refresh) this theme and the person's words, and confirm quietly.
  const keepIntentionClose = () => {
    const label = helpLabel || (THEMED_PRAYERS[helpTheme] ? THEMED_PRAYERS[helpTheme].label : 'A prayer');
    const updated = rememberIntention({ theme: helpTheme, words: helpText, label });
    setIntentions(updated);
    // Track the id so a later "again" from Today lines up with this same one.
    const saved = updated.find((it) => it.theme === helpTheme);
    if (saved) setHelpSourceIntentionId(saved.id);
    setIntentionSaved(true);
  };

  // "That's answered now" on the Today card: gently let an intention go, with a
  // brief warm acknowledgement.
  const letIntentionGo = (id) => {
    setIntentions(resolveIntention(id));
    setResolvedNote('Thanks be to God.');
    if (resolveTimer.current) clearTimeout(resolveTimer.current);
    resolveTimer.current = setTimeout(() => setResolvedNote(''), 2600);
  };

  const showSuccessfulPurchase = async () => {
    syncTierFromStorage();
    setPurchaseComplete(true);
    setPaywallMessage('');

    if (account && account.id) {
      setAccountBusy(true);
      setAccountMessage('Beloved Plus is active. Protecting it with your account...');
      const linked = await identifyPurchasesUser(account.id);
      setAccountBusy(false);
      if (linked.status === 'success') {
        setAccountLinked(true);
        setAccountMessage('Your Beloved Plus purchase is protected by your account.');
      } else {
        setAccountLinked(false);
        setAccountMessage('Plus is active on this device. We could not protect it just now; please try again.');
      }
    } else if (canUseAccounts() && /^\S+@\S+\.\S+$/.test(normalizeAccountEmail(accountEmail))) {
      // The receipt email remains prefilled, but account creation is always a
      // separate explicit choice: no Supabase request is sent until the person
      // taps the account panel's "Email me a sign-in link" button.
      setAccountMessage('Your receipt email is ready below. Choose “Email me a sign-in link” to protect this purchase.');
    }
  };

  const continueAfterPurchase = () => {
    if (paywallContext === 'settings') {
      setSettingsMessage('Beloved Plus is active - thank you.');
      closeSettingsPaywall();
    } else {
      generateHelpPrayer();
    }
  };

  // The plan's fixed re-check rhythm after payment evidence: quick first look,
  // then two patient ones. Total ~10 seconds before we reassure instead.
  const ACTIVATION_RETRY_DELAYS_MS = [2000, 4000, 4000];

  // Wait, then re-check the store for the entitlement. Sequential, cancellable
  // by a newer run (e.g. the person pressed "Check again" or left the paywall).
  const beginPaidActivation = async () => {
    const run = ++activationRun.current;
    setActivationState('activating');
    for (const delayMs of ACTIVATION_RETRY_DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      if (run !== activationRun.current) return;
      const result = await refreshTier();
      if (run !== activationRun.current) return;
      if (result.status === 'success') {
        setActivationState('');
        await showSuccessfulPurchase();
        setShowPlusBlessing(true);
        return;
      }
    }
    if (run === activationRun.current) setActivationState('stalled');
  };

  const checkActivationAgain = async () => {
    const run = ++activationRun.current;
    setActivationState('activating');
    const result = await refreshTier();
    if (run !== activationRun.current) return;
    if (result.status === 'success') {
      setActivationState('');
      await showSuccessfulPurchase();
      setShowPlusBlessing(true);
    } else {
      setActivationState('stalled');
    }
  };

  // The blessing settles on its own; tapping it skips sooner.
  useEffect(() => {
    if (!showPlusBlessing) return undefined;
    const timer = setTimeout(() => setShowPlusBlessing(false), 4500);
    return () => clearTimeout(timer);
  }, [showPlusBlessing]);

  // Buy before asking for an account. RevenueCat keeps a stable anonymous
  // customer, unlocks Plus immediately, then the success step offers optional
  // account protection without blocking the prayer they came for.
  const startBelovedPlus = async () => {
    const receiptEmail = account && account.email ? account.email : normalizeAccountEmail(accountEmail);
    if (canPurchaseOnWeb() && !/^\S+@\S+\.\S+$/.test(receiptEmail)) {
      setPaywallMessage('Enter the email address you want on your receipt.');
      return;
    }
    setPaywallBusy(true);
    setPaywallMessage('');
    const result = await purchasePlus(paywallPlan, receiptEmail);
    setPaywallBusy(false);
    if (result.status === 'success') {
      setPurchaseRedemptionUrl(
        safeHttpsUrl(result.redemptionInfo && result.redemptionInfo.redeemUrl)
      );
      await showSuccessfulPurchase();
      setShowPlusBlessing(true);
    } else if (result.status === 'paid_pending' || result.status === 'already_owned') {
      await beginPaidActivation();
    } else if (result.status === 'unavailable') {
      setPaywallMessage('Beloved Plus purchases aren\u2019t available just now.');
    } else if (result.status === 'cancelled') {
      setPaywallMessage('');
    } else {
      setPaywallMessage('We couldn’t complete that just now. Please try again in a moment.');
    }
  };

  // Restore a previous purchase (for someone on a new phone).
  const restoreBelovedPlus = async () => {
    setPaywallBusy(true);
    setPaywallMessage('');
    const result = await restorePlus();
    setPaywallBusy(false);
    if (result.status === 'success') {
      await showSuccessfulPurchase();
    } else if (result.status === 'unavailable') {
      setPaywallMessage('Beloved Plus purchases aren\u2019t available just now.');
    } else if (result.status === 'none') {
      setPaywallMessage('We couldn’t find a previous purchase to restore.');
    } else {
      setPaywallMessage('We couldn’t restore just now. Please try again in a moment.');
    }
  };

  // ============================================
  // Screen: Today
  // ============================================
  const renderToday = () => {
    const dailyPrayerButton = isEveningTime()
      ? { prayer: EVENING_PRAYER, label: 'Evening Prayer', icon: 'moon' }
      : { prayer: MORNING_PRAYER, label: 'Morning Prayer', icon: 'sunrise' };

    const verseSaved = isVerseSaved(dailyVerse.id);

    return (
      <div>
        <h2 style={{ fontSize: scaleFont(26), color: COLORS.cream, fontWeight: 400, margin: '4px 0 22px' }}>
          {getGreeting()}
        </h2>

        {/* Beloved's clearest promise stays visible before the longer daily content. */}
        <button
          onClick={startHelp}
          style={{ ...primaryButtonStyle, minHeight: '68px', fontSize: scaleFont(21), marginBottom: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
        >
          <Icon name="heart" size={26} color={COLORS.bg} strokeWidth={2} />
          Help Me Pray
        </button>

        {/* Gentle daily-reminder opt-in (native app only, shown once) */}
        {showReminderCard && (
          <section
            style={{
              background: COLORS.bgCard,
              borderRadius: '22px',
              padding: '24px 22px',
              marginBottom: '26px',
              border: `1px solid ${COLORS.border}`,
            }}
          >
            <p style={{ fontSize: scaleFont(20), color: COLORS.cream, fontWeight: 400, margin: '0 0 10px' }}>
              Would you like a gentle daily reminder?
            </p>
            <p style={{ fontSize: scaleFont(17), lineHeight: 1.6, color: COLORS.secondary, margin: '0 0 20px' }}>
              We can send you two quiet notes each day — one in the morning with today&apos;s scripture,
              and one in the evening to pause and pray.
            </p>
            <button
              onClick={handleTurnOnReminders}
              style={{ ...primaryButtonStyle, marginBottom: '12px' }}
            >
              Turn on
            </button>
            <button
              onClick={handleDismissReminders}
              style={{
                ...secondaryButtonStyle,
                border: `1px solid ${COLORS.border}`,
                background: 'none',
                color: COLORS.secondary,
              }}
            >
              Later
            </button>
          </section>
        )}

        {/* Today's Scripture */}
        <section
          style={{
            background: `linear-gradient(160deg, ${COLORS.bgCard} 0%, ${COLORS.bgLight} 100%)`,
            borderRadius: '22px',
            marginBottom: '26px',
            border: `1px solid ${COLORS.gold}`,
            boxShadow: '0 0 30px rgba(212,175,55,0.12)',
            overflow: 'hidden',
          }}
        >
          {/* Daily masterpiece art band — decorative strip that melts into the
              card. Verse text never sits on top of the image (it is rendered in
              the padded body below). */}
          {dailyArtwork && (
            <div
              role="img"
              aria-label={`${dailyArtwork.name} by ${dailyArtwork.artist}`}
              style={{
                position: 'relative',
                height: '120px',
                backgroundImage: `url(${dailyArtwork.file})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center 30%',
              }}
            >
              {/* Gradient melt to the card colour at the bottom. */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `linear-gradient(180deg, rgba(13,13,18,0.10) 0%, rgba(13,13,18,0.45) 55%, ${COLORS.bgCard} 100%)`,
                }}
              />
              {/* Tiny decorative caption, bottom-right (artist, year). */}
              <span
                style={{
                  position: 'absolute',
                  right: '14px',
                  bottom: '10px',
                  fontSize: '13px',
                  color: COLORS.gold,
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  letterSpacing: '0.5px',
                  textShadow: '0 1px 4px rgba(0,0,0,0.85)',
                }}
              >
                {dailyArtwork.artist}, {dailyArtwork.year}
              </span>
            </div>
          )}
          <div style={{ padding: '20px 24px 26px' }}>
          <p style={{ fontSize: scaleFont(15), color: COLORS.gold, letterSpacing: '2px', margin: '0 0 14px', fontWeight: 600 }}>
            TODAY&apos;S SCRIPTURE
          </p>
          <p style={{ fontSize: scaleFont(21), fontStyle: 'italic', lineHeight: 1.7, color: COLORS.cream, margin: '0 0 14px' }}>
            “{dailyVerse.text}”
          </p>
          <p style={{ fontSize: scaleFont(17), color: COLORS.gold, fontWeight: 600, margin: 0 }}>{dailyVerse.ref}</p>
          {dailyVerse.reflection && (
            <p style={{ fontSize: scaleFont(17), lineHeight: 1.6, color: COLORS.secondary, marginTop: '14px' }}>
              {dailyVerse.reflection}
            </p>
          )}
          {!verseSaved && (
            <button
              onClick={() => saveVerse(dailyVerse)}
              style={{
                ...secondaryButtonStyle,
                marginTop: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                background: 'rgba(212,175,55,0.08)',
                color: COLORS.gold,
              }}
            >
              <Icon name="saved" size={22} />
              Save this verse
            </button>
          )}
          {verseSaved && saveNotice === `verse:${dailyVerse.id}` && (
            <p role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: scaleFont(16), color: COLORS.gold, margin: '20px 4px 0' }}>
              <Icon name="check" size={20} />
              Kept in your quiet place
            </p>
          )}
          <button
            onClick={handleShareVerse}
            disabled={shareBusy}
            style={{
              ...secondaryButtonStyle,
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              border: `1px solid ${COLORS.border}`,
              background: 'none',
              color: COLORS.cream,
              opacity: shareBusy ? 0.65 : 1,
            }}
          >
            <Icon name="share" size={22} color={COLORS.gold} />
            {shareBusy ? 'Preparing share…' : 'Share today’s verse'}
          </button>
          {renderShareFeedback()}
          </div>
        </section>

        {/* A quiet, single-line door to Beloved Plus — free users only. */}
        {tier !== 'plus' && (
          <button
            type="button"
            onClick={openPlusFromSettings}
            aria-label="Beloved Plus — never be lost for words"
            style={{
              width: '100%',
              textAlign: 'left',
              background: 'rgba(212,175,55,0.06)',
              border: `1px solid ${COLORS.gold}`,
              borderRadius: '22px',
              padding: '20px 22px',
              marginBottom: '26px',
              cursor: 'pointer',
              fontFamily: SERIF,
            }}
          >
            <p style={{ fontSize: scaleFont(14), color: COLORS.gold, letterSpacing: '1.5px', textTransform: 'uppercase', margin: '0 0 6px' }}>
              Beloved Plus
            </p>
            <p style={{ fontSize: scaleFont(17), color: COLORS.cream, lineHeight: 1.5, margin: 0 }}>
              Never be lost for words — personal prayers from £5.99.
            </p>
          </button>
        )}

        {/* Still on your heart — the most recent remembered intention */}
        {(intentions.length > 0 || resolvedNote) && (
          <section
            style={{
              background: COLORS.bgCard,
              borderRadius: '22px',
              padding: '24px 22px',
              marginBottom: '26px',
              border: `1px solid ${COLORS.border}`,
            }}
          >
            {resolvedNote ? (
              <p
                style={{
                  fontSize: scaleFont(19),
                  color: COLORS.cream,
                  textAlign: 'center',
                  lineHeight: 1.6,
                  margin: 0,
                  fontStyle: 'italic',
                }}
              >
                {resolvedNote}
              </p>
            ) : (
              <>
                <p style={{ fontSize: scaleFont(15), color: COLORS.gold, letterSpacing: '2px', margin: '0 0 14px', fontWeight: 600 }}>
                  STILL ON YOUR HEART
                </p>
                <button
                  onClick={() => continuePrayingIntention(intentions[0])}
                  style={{
                    ...secondaryButtonStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    textAlign: 'center',
                  }}
                >
                  <Icon name="heart" size={24} color={COLORS.gold} />
                  Continue praying for {intentions[0].label}
                </button>
                <button
                  onClick={() => letIntentionGo(intentions[0].id)}
                  style={{
                    width: '100%',
                    minHeight: '48px',
                    marginTop: '10px',
                    background: 'none',
                    border: 'none',
                    color: COLORS.secondary,
                    fontSize: scaleFont(16),
                    fontFamily: SERIF,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  That&apos;s answered now
                </button>
              </>
            )}
          </section>
        )}

        {/* One time-appropriate daily prayer; the Pray tab contains the full library. */}
        <button
          onClick={() => openSetPrayer(dailyPrayerButton.prayer)}
          style={{ ...secondaryButtonStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
        >
          <Icon name={dailyPrayerButton.icon} size={24} />
          {dailyPrayerButton.label}
        </button>
      </div>
    );
  };

  // ============================================
  // Screen: Pray
  // ============================================
  const renderPray = () => (
    <div>
      <h2 style={{ fontSize: scaleFont(24), color: COLORS.cream, fontWeight: 400, margin: '4px 0 20px' }}>Pray</h2>

      {/* Daily rhythm */}
      <button
        onClick={() => openSetPrayer(MORNING_PRAYER)}
        style={{ ...secondaryButtonStyle, marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
      >
        <Icon name="sunrise" size={24} />
        Morning Prayer
      </button>
      <button
        onClick={() => openSetPrayer(EVENING_PRAYER)}
        style={{ ...secondaryButtonStyle, marginBottom: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}
      >
        <Icon name="moon" size={24} />
        Evening Prayer
      </button>

      {/* Prayers by theme */}
      <h3 style={{ fontSize: scaleFont(18), color: COLORS.gold, margin: '0 0 14px', letterSpacing: '1px' }}>Prayers for a need</h3>
      {THEME_ORDER.map((themeId) => {
        const theme = THEMED_PRAYERS[themeId];
        return (
          <button key={themeId} onClick={() => openThemedPrayer(themeId, 0)} style={rowStyle}>
            <span style={{ color: COLORS.gold, display: 'flex' }}>
              <Icon name={THEME_ICON[themeId]} size={28} />
            </span>
            <span style={{ flex: 1, fontSize: scaleFont(19), color: COLORS.text }}>{theme.label}</span>
            <span style={{ color: COLORS.gold, display: 'flex' }}>
              <Icon name="chevron" size={22} />
            </span>
          </button>
        );
      })}

      {/* Prayers from the Bible */}
      <h3 style={{ fontSize: scaleFont(18), color: COLORS.gold, margin: '28px 0 14px', letterSpacing: '1px' }}>Prayers from the Bible</h3>
      {BIBLICAL_PRAYERS.map((prayer) => (
        <button key={prayer.id} onClick={() => openSetPrayer(prayer)} style={rowStyle}>
          <span style={{ color: COLORS.gold, display: 'flex' }}>
            <Icon name="book" size={28} />
          </span>
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: scaleFont(19), color: COLORS.text }}>{prayer.title}</span>
            <span style={{ display: 'block', fontSize: scaleFont(15), color: COLORS.secondary, marginTop: '2px' }}>{prayer.subtitle}</span>
          </span>
          <span style={{ color: COLORS.gold, display: 'flex' }}>
            <Icon name="chevron" size={22} />
          </span>
        </button>
      ))}
    </div>
  );

  // ============================================
  // Screen: Saved
  // ============================================
  const renderSaved = () => {
    const nothingSaved = savedPrayers.length === 0 && savedVerses.length === 0;
    const totalSaved = savedPrayers.length + savedVerses.length;
    const previewText = (value, limit = 118) => {
      const clean = String(value || '').replace(/\s+/g, ' ').trim();
      return clean.length > limit ? `${clean.slice(0, limit).trim()}…` : clean;
    };

    return (
      <div className="beloved-saved-screen">
        <header className="beloved-saved-hero">
          <div className="beloved-saved-hero-icon" aria-hidden="true">
            <Icon name="saved" size={27} strokeWidth={1.5} />
          </div>
          <p className="beloved-saved-eyebrow" style={{ fontSize: scaleFont(13) }}>Your quiet place</p>
          <h2 style={{ fontSize: scaleFont(28), color: COLORS.cream, fontWeight: 400, margin: '5px 0 8px' }}>
            Saved for you
          </h2>
          <p style={{ fontSize: scaleFont(16), color: COLORS.secondary, lineHeight: 1.6, margin: 0 }}>
            Prayers and scripture to return to whenever you need them.
          </p>
          {!nothingSaved && (
            <p className="beloved-saved-total" aria-label={`${totalSaved} saved ${totalSaved === 1 ? 'item' : 'items'}`}>
              <strong>{totalSaved}</strong> {totalSaved === 1 ? 'keepsake' : 'keepsakes'}
            </p>
          )}
        </header>

        {nothingSaved && (
          <div className="beloved-saved-empty">
            <div style={{ color: COLORS.gold, display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
              <Icon name="saved" size={40} strokeWidth={1.4} />
            </div>
            <p style={{ fontSize: scaleFont(18), color: COLORS.cream, margin: '0 0 6px' }}>Nothing saved yet</p>
            <p style={{ fontSize: scaleFont(16), color: COLORS.secondary, margin: 0, lineHeight: 1.6 }}>
              When a prayer or verse comforts you, tap “Save” to keep it here.
            </p>
            <div className="beloved-saved-empty-actions">
              <button onClick={() => setActiveTab('pray')} style={{ ...primaryButtonStyle, marginTop: 0 }}>
                Find a prayer
              </button>
              <button onClick={() => setActiveTab('today')} style={{ ...secondaryButtonStyle, marginTop: 0 }}>
                See today&apos;s verse
              </button>
            </div>
          </div>
        )}

        {savedPrayers.length > 0 && (
          <section className="beloved-saved-section" aria-labelledby="saved-prayers-heading">
            <div className="beloved-saved-section-heading">
              <h3 id="saved-prayers-heading" style={{ fontSize: scaleFont(19), color: COLORS.cream, margin: 0, fontWeight: 400 }}>
                Prayers you&apos;ve kept
              </h3>
              <span aria-label={`${savedPrayers.length} saved prayers`}>{savedPrayers.length}</span>
            </div>
            {savedPrayers.map((prayer) => (
              <article className="beloved-saved-row beloved-saved-card" key={prayer.id} style={{ ...rowStyle, cursor: 'default', alignItems: 'flex-start', flexDirection: 'column', gap: '12px' }}>
                <button
                  className="beloved-saved-open"
                  onClick={() => openSavedPrayer(prayer)}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: SERIF }}
                >
                  <span className="beloved-saved-card-icon" style={{ color: COLORS.gold }}>
                    <Icon name="heart" size={23} />
                  </span>
                  <span className="beloved-saved-copy" style={{ flex: '1 1 0', minWidth: 0 }}>
                    {prayer.meta && (
                      <span className="beloved-saved-card-meta" style={{ fontSize: scaleFont(12) }}>{prayer.meta}</span>
                    )}
                    <span style={{ display: 'block', fontSize: scaleFont(20), color: COLORS.text, lineHeight: 1.35, marginTop: prayer.meta ? '5px' : 0, overflowWrap: 'anywhere' }}>{prayer.title}</span>
                    <span className="beloved-saved-card-preview" style={{ fontSize: scaleFont(15) }}>
                      {previewText(prayer.body)}
                    </span>
                  </span>
                  <span style={{ color: COLORS.gold, display: 'flex', flexShrink: 0, marginTop: '10px' }}>
                    <Icon name="chevron" size={22} />
                  </span>
                </button>
                <button
                  onClick={() => removePrayer(prayer.id)}
                  className="beloved-saved-remove"
                  aria-label={`Remove ${prayer.title} from saved prayers`}
                  style={{ fontSize: scaleFont(14), fontFamily: SERIF }}
                >
                  Remove from Saved
                </button>
              </article>
            ))}
          </section>
        )}

        {savedVerses.length > 0 && (
          <section className="beloved-saved-section" aria-labelledby="saved-verses-heading">
            <div className="beloved-saved-section-heading">
              <h3 id="saved-verses-heading" style={{ fontSize: scaleFont(19), color: COLORS.cream, margin: 0, fontWeight: 400 }}>
                Verses to return to
              </h3>
              <span aria-label={`${savedVerses.length} saved verses`}>{savedVerses.length}</span>
            </div>
            {savedVerses.map((verse) => (
              <article className="beloved-saved-row beloved-saved-card" key={verse.id} style={{ ...rowStyle, cursor: 'default', alignItems: 'flex-start', flexDirection: 'column', gap: '12px' }}>
                <button
                  className="beloved-saved-open"
                  onClick={() => openSavedVerse(verse)}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: SERIF }}
                >
                  <span className="beloved-saved-card-icon" style={{ color: COLORS.gold }}>
                    <Icon name="book" size={23} />
                  </span>
                  <span className="beloved-saved-copy" style={{ flex: '1 1 0', minWidth: 0 }}>
                    <span className="beloved-saved-card-meta" style={{ fontSize: scaleFont(12) }}>Scripture</span>
                    <span style={{ display: 'block', fontSize: scaleFont(20), color: COLORS.text, lineHeight: 1.35, marginTop: '5px', overflowWrap: 'anywhere' }}>{verse.ref}</span>
                    <span className="beloved-saved-card-preview beloved-saved-verse-preview" style={{ fontSize: scaleFont(15) }}>
                      “{previewText(verse.text)}”
                    </span>
                  </span>
                  <span style={{ color: COLORS.gold, display: 'flex', flexShrink: 0, marginTop: '10px' }}>
                    <Icon name="chevron" size={22} />
                  </span>
                </button>
                <button
                  onClick={() => removeVerse(verse.id)}
                  className="beloved-saved-remove"
                  aria-label={`Remove ${verse.ref} from saved verses`}
                  style={{ fontSize: scaleFont(14), fontFamily: SERIF }}
                >
                  Remove from Saved
                </button>
              </article>
            ))}
          </section>
        )}
      </div>
    );
  };
  // ============================================
  // Full-screen prayer reader
  // ============================================
  const renderReader = () => {
    if (!activePrayer) return null;
    const saved = isPrayerSaved(activePrayer.id);
    const theme = activePrayer.themeId ? THEMED_PRAYERS[activePrayer.themeId] : null;
    const canReadAnother = theme && theme.prayers.length > 1;

    return (
      <div style={overlayStyle}>
        <div style={overlayInnerStyle}>
          <button onClick={closeReader} style={backButtonStyle} aria-label="Go back">
            <Icon name="back" size={22} />
            Back
          </button>

          <h2 style={{ fontSize: scaleFont(26), color: COLORS.gold, fontWeight: 400, textAlign: 'center', margin: '8px 0 6px', textShadow: GOLD_HALO }}>
            {activePrayer.title}
          </h2>
          {activePrayer.meta && (
            <p style={{ fontSize: scaleFont(16), color: COLORS.secondary, textAlign: 'center', margin: '0 0 22px' }}>{activePrayer.meta}</p>
          )}

          <PrayerBody text={activePrayer.body} />

          {activePrayer.reflection && (
            <p style={{ fontSize: scaleFont(17), color: COLORS.secondary, textAlign: 'center', lineHeight: 1.6, marginTop: '18px' }}>
              {activePrayer.reflection}
            </p>
          )}

          {canSpeak() && (
            <button
              onClick={() => toggleReadAloud(activePrayer.title, activePrayer.body)}
              aria-label={isSpeaking ? (speechPreparing ? 'Cancel voice preparation' : 'Stop reading aloud') : 'Read this prayer aloud in a calm, reverent voice'}
              style={{
                ...secondaryButtonStyle,
                marginTop: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                background: isSpeaking ? COLORS.gold : 'rgba(212,175,55,0.08)',
                color: isSpeaking ? COLORS.bg : COLORS.gold,
              }}
            >
              <Icon name="speaker" size={22} color={isSpeaking ? COLORS.bg : COLORS.gold} />
              {isSpeaking ? (speechPreparing ? 'Preparing voice…' : 'Stop') : usesEnhancedVoice() ? 'Gentle AI read aloud' : 'Reverent read aloud'}
            </button>
          )}

          {canSpeak() && usesEnhancedVoice() && (
            <p style={{ fontSize: scaleFont(14), color: COLORS.secondary, textAlign: 'center', lineHeight: 1.5, margin: '10px 8px 0' }}>
              AI-generated voice. Your words are processed only to create this reading and are not stored by Beloved.
            </p>
          )}

          <button
            onClick={() => handleSharePrayer(activePrayer)}
            disabled={shareBusy}
            style={{
              ...secondaryButtonStyle,
              marginTop: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              border: `1px solid ${COLORS.border}`,
              background: 'none',
              color: COLORS.cream,
              opacity: shareBusy ? 0.65 : 1,
            }}
          >
            <Icon name="share" size={22} color={COLORS.gold} />
            {shareBusy ? 'Preparing share…' : 'Share this prayer'}
          </button>
          {renderPrayerShareConfirmation(activePrayer)}
          {renderShareFeedback()}

          {canReadAnother && (
            <button
              onClick={() => openThemedPrayer(activePrayer.themeId, (activePrayer.index + 1) % theme.prayers.length)}
              style={{ ...secondaryButtonStyle, border: 'none', background: 'none', color: COLORS.gold, marginTop: '18px', textDecoration: 'underline', fontSize: scaleFont(17) }}
            >
              Read another prayer
            </button>
          )}

          {activePrayer.canSave && !saved && (
            <button
              onClick={() =>
                savePrayer({ id: activePrayer.id, title: activePrayer.title, meta: activePrayer.meta, body: activePrayer.body })
              }
              style={{
                ...secondaryButtonStyle,
                marginTop: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                background: 'rgba(212,175,55,0.08)',
                color: COLORS.gold,
              }}
            >
              <Icon name="saved" size={22} />
              Save this prayer
            </button>
          )}

          {activePrayer.canSave && saved && saveNotice === `prayer:${activePrayer.id}` && (
            <p
              role="status"
              aria-live="polite"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: scaleFont(16), color: COLORS.gold, textAlign: 'center', lineHeight: 1.55, margin: '18px 4px 0' }}
            >
              <Icon name="check" size={20} />
              Kept in your quiet place
            </p>
          )}

          <button
            onClick={() => beginAmen(closeReader)}
            className="beloved-amen-button"
            style={{ ...primaryButtonStyle, marginTop: '18px' }}
          >
            Amen
          </button>

          <button
            onClick={returnToTodayFromReader}
            style={{ ...secondaryButtonStyle, marginTop: '14px', border: 'none', background: 'none', color: COLORS.gold }}
          >
            Back to Today
          </button>
        </div>
      </div>
    );
  };

  // ============================================
  // Full-screen "Help Me Pray" journey
  // ============================================
  const renderHelp = () => {
    if (helpStep === null) return null;
    const remainingPersonalPrayers = aiPrayersRemaining(hasPrayerAccount);
    const allowance = freeAllowance(hasPrayerAccount);
    const freeTier = tier !== 'plus';
    const showUrgentSupport = needsUrgentSupport(helpText);

    const renderUrgentSupport = () => {
      if (!showUrgentSupport) return null;
      return (
        <div
          role="alert"
          aria-label="Urgent support"
          style={{
            background: 'rgba(232,201,107,0.10)',
            border: `2px solid ${COLORS.goldLight}`,
            borderRadius: '16px',
            padding: '18px',
            margin: '16px 0',
          }}
        >
          <p style={{ fontSize: scaleFont(19), color: COLORS.cream, fontWeight: 700, margin: '0 0 8px' }}>
            Your safety matters
          </p>
          <p style={{ fontSize: scaleFont(17), color: COLORS.text, lineHeight: 1.65, margin: 0 }}>
            {URGENT_SUPPORT_MESSAGE}
          </p>
        </div>
      );
    };

    const onBack = () => {
      if (helpStep === 1) closeHelp();
      else if (helpStep === 'paywall' && paywallContext === 'settings') closeSettingsPaywall();
      else if (helpStep === 'paywall') setHelpStep(2);
      // Back from the sign-in step returns to their own words, not the start.
      else if (helpStep === 'signin') setHelpStep(2);
      else setHelpStep(1);
    };

    return (
      <div style={overlayStyle}>
        <div style={overlayInnerStyle}>
          {/* Back is always visible */}
          <button onClick={onBack} style={backButtonStyle} aria-label="Go back">
            <Icon name="back" size={22} />
            Back
          </button>

          {/* Step 1 — choose what's on your heart */}
          {helpStep === 1 && (
            <div>
              <h2 style={{ fontSize: scaleFont(26), color: COLORS.gold, fontWeight: 400, textAlign: 'center', margin: '8px 0 6px', textShadow: GOLD_HALO }}>
                What&apos;s on your heart?
              </h2>
              <p style={{ fontSize: scaleFont(17), color: COLORS.secondary, textAlign: 'center', margin: '0 0 24px' }}>
                Choose one to begin.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px' }}>
                {HELP_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => chooseHelpOption(option)}
                    style={{ ...rowStyle, minHeight: '72px', marginBottom: 0 }}
                  >
                    <span style={{ color: COLORS.gold, display: 'flex' }}>
                      <Icon name={option.icon} size={30} />
                    </span>
                    <span style={{ flex: 1, fontSize: scaleFont(20), color: COLORS.text }}>{option.label}</span>
                    <span style={{ color: COLORS.gold, display: 'flex' }}>
                      <Icon name="chevron" size={22} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — tell Beloved what's happening */}
          {helpStep === 2 && (
            <div style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
              <h2 style={{ fontSize: scaleFont(25), color: COLORS.gold, fontWeight: 400, textAlign: 'center', margin: '8px 0 6px', textShadow: GOLD_HALO }}>
                Tell Beloved what&apos;s happening
              </h2>
              <p style={{ fontSize: scaleFont(17), color: COLORS.secondary, textAlign: 'center', margin: '0 0 20px' }}>
                Write as much or as little as you like. You can leave this empty.
              </p>
              <textarea
                value={helpText}
                onChange={(e) => setHelpText(e.target.value)}
                aria-describedby={`prayer-processing-notice${freeTier ? ' prayer-allowance-notice' : ''}`}
                onFocus={(e) => {
                  const el = e.target;
                  setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                }}
                placeholder="Whatever is on your mind…"
                style={{
                  width: '100%',
                  minHeight: '180px',
                  padding: '18px',
                  borderRadius: '16px',
                  border: `1px solid ${COLORS.gold}`,
                  background: COLORS.bgLight,
                  color: COLORS.text,
                  fontSize: scaleFont(19),
                  lineHeight: 1.6,
                  fontFamily: SERIF,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              {renderUrgentSupport()}
              <p
                id="prayer-processing-notice"
                style={{
                  fontSize: scaleFont(16),
                  color: COLORS.secondary,
                  lineHeight: 1.55,
                  margin: '12px 4px 0',
                }}
              >
                To write your prayer, these words are sent securely to our prayer-writing provider.
                Beloved does not store them.
              </p>
              {canListen() && (
                <>
                  <button
                    onClick={toggleListening}
                    aria-label={isListening ? 'Stop speaking and finish' : 'Speak instead of typing'}
                    aria-describedby={speechInputMessage ? 'speech-input-message' : undefined}
                    style={{
                      ...secondaryButtonStyle,
                      marginTop: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      background: isListening ? COLORS.gold : 'rgba(212,175,55,0.08)',
                      color: isListening ? COLORS.bg : COLORS.gold,
                    }}
                  >
                    <Icon name="mic" size={22} color={isListening ? COLORS.bg : COLORS.gold} />
                    {isListening ? 'Listening… tap to finish' : 'Speak instead'}
                  </button>
                  {speechInputMessage && (
                    <p
                      id="speech-input-message"
                      role={speechInputHasError ? 'alert' : 'status'}
                      aria-live="polite"
                      style={{
                        fontSize: scaleFont(16),
                        color: speechInputHasError ? COLORS.cream : COLORS.secondary,
                        lineHeight: 1.55,
                        margin: '10px 4px 0',
                      }}
                    >
                      {speechInputMessage}
                    </p>
                  )}
                </>
              )}
              {!canListen() && (
                <p
                  role="status"
                  style={{
                    fontSize: scaleFont(16),
                    color: COLORS.secondary,
                    lineHeight: 1.55,
                    margin: '14px 4px 0',
                  }}
                >
                  To speak your words, tap the microphone on your phone keyboard. This browser does not offer direct voice input.
                </p>
              )}
              <div style={{ flex: 1 }} />
              {freeTier && (
                <p
                  id="prayer-allowance-notice"
                  aria-live="polite"
                  style={{
                    fontSize: scaleFont(16),
                    color: remainingPersonalPrayers > 0 ? COLORS.secondary : COLORS.gold,
                    textAlign: 'center',
                    lineHeight: 1.55,
                    margin: '18px 4px 0',
                  }}
                >
                  {allowance.period === 'ever'
                    ? remainingPersonalPrayers > 0
                      ? 'Your first personal prayer is free.'
                      : 'A free account opens 3 personal prayers a month.'
                    : remainingPersonalPrayers === 1
                      ? '1 personal prayer remaining this month.'
                      : `${remainingPersonalPrayers} of ${FREE_AI_PRAYER_LIMIT} personal prayers remaining this month.`}
                </p>
              )}
              <button onClick={continueFromDetails} style={{ ...primaryButtonStyle, marginTop: '20px' }}>
                Continue
              </button>
            </div>
          )}

          {/* Step 3 — preparing, then the prayer */}
          {helpStep === 3 && (
            <div>
              {helpLoading || !helpPrayer ? (
                <>
                  <PrayerCreationVisual phase={prayerVisualPhase} />
                  <div aria-hidden="true" style={{ display: 'none' }}>
                  <div style={{ color: COLORS.gold, display: 'flex', justifyContent: 'center', marginBottom: '22px' }}>
                    <Icon name="heart" size={44} strokeWidth={1.4} />
                  </div>
                  <p style={{ fontSize: scaleFont(21), color: COLORS.cream, fontStyle: 'italic', fontFamily: SERIF }}>
                    Preparing your prayer…
                  </p>
                  </div>
                </>
              ) : (
                <div>
                  <h2 style={{ fontSize: scaleFont(26), color: COLORS.gold, fontWeight: 400, textAlign: 'center', margin: '8px 0 6px', textShadow: GOLD_HALO }}>
                    {helpPrayer.title}
                  </h2>
                  <p style={{ fontSize: scaleFont(16), color: COLORS.secondary, textAlign: 'center', margin: '0 0 22px' }}>{helpPrayer.meta}</p>

                  {renderUrgentSupport()}
                  <PrayerBody text={helpPrayer.body} />

                  {helpPrayer.note && (
                    <p
                      role="status"
                      aria-live="polite"
                      style={{
                        fontSize: scaleFont(16),
                        color: COLORS.secondary,
                        textAlign: 'center',
                        lineHeight: 1.6,
                        margin: '18px 4px 0',
                      }}
                    >
                      {helpPrayer.note}
                    </p>
                  )}

                  {helpPrayer.retryable && (
                    <button
                      onClick={generateHelpPrayer}
                      style={{ ...secondaryButtonStyle, marginTop: '14px' }}
                    >
                      Try personalising again
                    </button>
                  )}

                  {canSpeak() && (
                    <button
                      onClick={() => toggleReadAloud(helpPrayer.title, helpPrayer.body)}
                      aria-label={isSpeaking ? (speechPreparing ? 'Cancel voice preparation' : 'Stop reading aloud') : 'Read this prayer aloud in a calm, reverent voice'}
                      style={{
                        ...secondaryButtonStyle,
                        marginTop: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        background: isSpeaking ? COLORS.gold : 'rgba(212,175,55,0.08)',
                        color: isSpeaking ? COLORS.bg : COLORS.gold,
                      }}
                    >
                      <Icon name="speaker" size={22} color={isSpeaking ? COLORS.bg : COLORS.gold} />
                      {isSpeaking ? (speechPreparing ? 'Preparing voice…' : 'Stop') : usesEnhancedVoice() ? 'Gentle AI read aloud' : 'Reverent read aloud'}
                    </button>
                  )}

                  {canSpeak() && usesEnhancedVoice() && (
                    <p style={{ fontSize: scaleFont(14), color: COLORS.secondary, textAlign: 'center', lineHeight: 1.5, margin: '10px 8px 0' }}>
                      AI-generated voice. Your words are processed only to create this reading and are not stored by Beloved.
                    </p>
                  )}

                  <button
                    onClick={() => handleSharePrayer(helpPrayer)}
                    disabled={shareBusy}
                    style={{
                      ...secondaryButtonStyle,
                      marginTop: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      border: `1px solid ${COLORS.border}`,
                      background: 'none',
                      color: COLORS.cream,
                      opacity: shareBusy ? 0.65 : 1,
                    }}
                  >
                    <Icon name="share" size={22} color={COLORS.gold} />
                    {shareBusy ? 'Preparing share…' : 'Share this prayer'}
                  </button>
                  {renderPrayerShareConfirmation(helpPrayer)}
                  {renderShareFeedback()}

                  {!isPrayerSaved(helpPrayer.id) ? (
                    <button
                      onClick={() => savePrayer(helpPrayer)}
                      style={{
                        ...secondaryButtonStyle,
                        marginTop: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                      }}
                    >
                      <Icon name="saved" size={22} />
                      Save this prayer
                    </button>
                  ) : saveNotice === `prayer:${helpPrayer.id}` ? (
                    <p role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: scaleFont(16), color: COLORS.gold, textAlign: 'center', lineHeight: 1.55, margin: '18px 4px 0' }}>
                      <Icon name="check" size={20} />
                      Kept in your quiet place
                    </p>
                  ) : null}

                  {/* Keep this close — remember the intention to pray again */}
                  {intentionSaved ? (
                    <p
                      style={{
                        fontSize: scaleFont(17),
                        color: COLORS.gold,
                        textAlign: 'center',
                        lineHeight: 1.6,
                        margin: '18px 4px 0',
                        fontStyle: 'italic',
                      }}
                    >
                      Beloved will keep this close.
                    </p>
                  ) : (
                    <button
                      onClick={keepIntentionClose}
                      style={{
                        ...secondaryButtonStyle,
                        marginTop: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        border: `1px solid ${COLORS.border}`,
                      }}
                    >
                      <Icon name="heart" size={22} color={COLORS.gold} />
                      Pray about this again tomorrow
                    </button>
                  )}

                  <button
                    onClick={restartHelpChoice}
                    style={{ ...secondaryButtonStyle, border: 'none', background: 'none', color: COLORS.gold, marginTop: '14px', textDecoration: 'underline', fontSize: scaleFont(17) }}
                  >
                    Pray for something else
                  </button>

                  <button
                    onClick={() => beginAmen(closeHelpAfterAmen)}
                    className="beloved-amen-button"
                    style={{ ...primaryButtonStyle, marginTop: '14px' }}
                  >
                    Amen
                  </button>

                  <button
                    onClick={closeHelp}
                    style={{ ...secondaryButtonStyle, marginTop: '14px', border: 'none', background: 'none', color: COLORS.gold }}
                  >
                    Back to Today
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Sign-in — reached once the free try-it prayer has been used and
              no account is signed in. The prayer they wrote is waiting: the
              moment they sign in, Beloved carries straight on. */}
          {helpStep === 'signin' && renderPrayerSignIn()}

          {/* Paywall — reached only when a signed-in free person's 3 monthly
              personal prayers are used up. Nothing else in Beloved is gated. */}
          {helpStep === 'paywall' && renderPaywall()}
        </div>
      </div>
    );
  };

  // ============================================
  const renderAccountPanel = (location) => {
    const inputId = `beloved-account-email-${location}`;
    const codeInputId = `beloved-account-code-${location}`;
    const protectingPurchase = location === 'purchase';
    const returnTo = protectingPurchase ? 'protect' : 'settings';

    if (!canUseAccounts()) {
      return <p style={{ fontSize: scaleFont(17), color: COLORS.secondary, lineHeight: 1.6, margin: 0 }}>Account sign-in isn't available just now.</p>;
    }

    if (!accountReady) {
      return <p role="status" style={{ fontSize: scaleFont(17), color: COLORS.secondary, lineHeight: 1.6, margin: 0 }}>Checking your account...</p>;
    }

    if (account) {
      return (
        <div>
          <p style={{ fontSize: scaleFont(17), color: COLORS.cream, lineHeight: 1.6, margin: 0, overflowWrap: 'anywhere' }}>
            Signed in as <strong>{account.email}</strong>
          </p>
          {accountLinked && tier === 'plus' && (
            <p style={{ fontSize: scaleFont(16), color: COLORS.goldLight, lineHeight: 1.55, margin: '8px 0 0' }}>
              Your Beloved Plus purchase is protected.
            </p>
          )}
          {!accountLinked && tier === 'plus' && (
            <button
              type="button"
              onClick={retryAccountProtection}
              disabled={accountBusy}
              style={{ ...secondaryButtonStyle, marginTop: '14px', opacity: accountBusy ? 0.7 : 1 }}
            >
              {accountBusy ? 'Checking purchase...' : 'Try protecting this purchase again'}
            </button>
          )}
          {location === 'settings' && (
            <>
              <p style={{ fontSize: scaleFont(16), color: COLORS.secondary, lineHeight: 1.55, margin: '8px 0 16px' }}>
                Your Beloved Plus access can follow you between the website and your other devices.
              </p>
              <p style={{ fontSize: scaleFont(16), color: COLORS.cream, lineHeight: 1.55, margin: '0 0 16px' }}>
                Signing out does not remove saved prayers or prayer intentions from this device. If this is a shared device, clear them below.
              </p>
              <button type="button" onClick={handleAccountSignOut} disabled={accountBusy} style={{ ...secondaryButtonStyle, marginBottom: '10px', opacity: accountBusy ? 0.7 : 1 }}>
                {accountBusy ? 'One moment...' : 'Sign out'}
              </button>
              <button type="button" onClick={() => requestAccountDeletion(account.email)} style={{ ...secondaryButtonStyle, background: 'none', border: 'none', color: COLORS.secondary }}>
                Request account deletion
              </button>
            </>
          )}
          {accountMessage && (
            <p role="status" aria-live="polite" style={{ fontSize: scaleFont(16), color: COLORS.cream, lineHeight: 1.55, margin: '12px 0 0' }}>{accountMessage}</p>
          )}
        </div>
      );
    }

    return (
      <div>
        <p style={{ fontSize: scaleFont(18), color: COLORS.cream, lineHeight: 1.55, margin: '0 0 6px' }}>
          {protectingPurchase ? 'Protect your purchase' : 'Create your free account or sign in'}
        </p>
        <p style={{ fontSize: scaleFont(16), color: COLORS.secondary, lineHeight: 1.55, margin: '0 0 14px' }}>
          {protectingPurchase
            ? 'Optional, but recommended. An account lets Plus follow you to another browser or device.'
            : 'We will email you a secure sign-in link. If your email also shows a six-digit code, you can enter it here instead. There is no password to remember.'}
        </p>

        {accountCodeSent ? (
          <form onSubmit={handleVerifyAccountCode}>
            <label htmlFor={codeInputId} style={{ display: 'block', fontSize: scaleFont(16), color: COLORS.secondary, marginBottom: '7px' }}>
              Six-digit code (if shown)
            </label>
            <input
              id={codeInputId}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={accountCode}
              onChange={(event) => setAccountCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              style={{ width: '100%', minHeight: '56px', boxSizing: 'border-box', borderRadius: '14px', border: `2px solid ${COLORS.border}`, background: COLORS.bgLight, color: COLORS.text, padding: '12px 14px', fontFamily: SERIF, fontSize: scaleFont(22), letterSpacing: '6px', textAlign: 'center', marginBottom: '12px' }}
            />
            <button type="submit" disabled={accountBusy || accountCode.length !== 6} style={{ ...primaryButtonStyle, opacity: accountBusy || accountCode.length !== 6 ? 0.7 : 1 }}>
              {accountBusy ? 'Checking...' : protectingPurchase ? 'Sign in and protect purchase' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={(event) => handleEmailSignIn(event, returnTo)}
              disabled={accountBusy}
              style={{ ...secondaryButtonStyle, marginTop: '10px', border: 'none', background: 'none', color: COLORS.gold }}
            >
              Send a new sign-in email
            </button>
            <button
              type="button"
              onClick={() => {
                setAccountCodeSent(false);
                setAccountCode('');
                setAccountMessage('');
              }}
              disabled={accountBusy}
              style={{ ...secondaryButtonStyle, border: 'none', background: 'none', color: COLORS.secondary }}
            >
              Use a different email
            </button>
          </form>
        ) : (
          <form onSubmit={(event) => handleEmailSignIn(event, returnTo)}>
            <label htmlFor={inputId} style={{ display: 'block', fontSize: scaleFont(16), color: COLORS.secondary, marginBottom: '7px' }}>Email address</label>
            <input
              id={inputId}
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={accountEmail}
              onChange={(event) => setAccountEmail(event.target.value)}
              placeholder="you@example.com"
              style={{ width: '100%', minHeight: '56px', boxSizing: 'border-box', borderRadius: '14px', border: `2px solid ${COLORS.border}`, background: COLORS.bgLight, color: COLORS.text, padding: '12px 14px', fontFamily: SERIF, fontSize: scaleFont(18), marginBottom: '12px' }}
            />
            <button type="submit" disabled={accountBusy} style={{ ...primaryButtonStyle, opacity: accountBusy ? 0.7 : 1 }}>
              {accountBusy ? 'Sending...' : 'Email me a sign-in link'}
            </button>
          </form>
        )}
        {accountMessage && (
          <p role="status" aria-live="polite" style={{ fontSize: scaleFont(16), color: COLORS.cream, lineHeight: 1.55, margin: '12px 0 0' }}>{accountMessage}</p>
        )}
      </div>
    );
  };

  // ============================================
  // The sign-in step of the Help journey. Reached once the free try-it prayer
  // has been used and no account is signed in. The words they wrote are held in
  // state throughout, so signing in resumes the prayer rather than restarting.
  // ============================================
  const renderPrayerSignIn = () => (
    <div>
      <h2
        style={{
          fontSize: scaleFont(27),
          color: COLORS.gold,
          fontWeight: 400,
          textAlign: 'center',
          margin: '8px 0 14px',
          textShadow: GOLD_HALO,
        }}
      >
        Your prayer is waiting
      </h2>
      <p
        style={{
          fontSize: scaleFont(18),
          color: COLORS.cream,
          lineHeight: 1.65,
          textAlign: 'center',
          margin: '0 0 8px',
        }}
      >
        You’ve used your free personal prayer. A free Beloved account opens{' '}
        {FREE_AI_PRAYER_LIMIT} a month — no payment, no password.
      </p>
      <p
        style={{
          fontSize: scaleFont(16),
          color: COLORS.secondary,
          lineHeight: 1.6,
          textAlign: 'center',
          margin: '0 0 24px',
        }}
      >
        Everything else in Beloved — today’s scripture, morning and evening
        prayer, the psalms, your saved prayers — stays free either way.
      </p>

      {renderAccountPanel('prayer')}

      <button
        type="button"
        onClick={dismissPaywallWithPrayer}
        style={{
          ...secondaryButtonStyle,
          marginTop: '18px',
          border: 'none',
          background: 'none',
          color: COLORS.secondary,
        }}
      >
        Maybe later — pray with me now
      </button>
    </div>
  );

  // Beloved Plus paywall (inside the Help journey or opened from Settings)
  // ============================================
  const renderPaywall = () => {
    const features = [
      'Prayers shaped around what you’re carrying',
      'Personal prayer whenever you need words',
      'Supporting Beloved’s mission',
    ];
    const selectedPlan = plusPlans.find((plan) => plan.id === paywallPlan) || plusPlans[0];

    if (activationState === 'activating') {
      return (
        <div role="status" aria-live="polite" style={{ textAlign: 'center', padding: '10px 4px' }}>
          <div style={{ color: COLORS.gold, display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
            <Icon name="check" size={40} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontSize: scaleFont(24), color: COLORS.gold, fontWeight: 400, margin: '0 0 10px', textShadow: GOLD_HALO }}>
            Payment received
          </h2>
          <p style={{ fontSize: scaleFont(18), color: COLORS.cream, lineHeight: 1.6, margin: 0 }}>
            Your payment went through — thank you. Beloved is finishing your activation…
          </p>
        </div>
      );
    }

    if (activationState === 'stalled') {
      return (
        <div style={{ textAlign: 'center', padding: '10px 4px' }}>
          <h2 style={{ fontSize: scaleFont(24), color: COLORS.gold, fontWeight: 400, margin: '0 0 10px', textShadow: GOLD_HALO }}>
            Payment received
          </h2>
          <p role="status" aria-live="polite" style={{ fontSize: scaleFont(18), color: COLORS.cream, lineHeight: 1.6, margin: '0 0 20px' }}>
            Your payment went through and your subscription is safe. Activation is taking longer than usual.
          </p>
          <button type="button" onClick={checkActivationAgain} style={primaryButtonStyle}>
            Check again
          </button>
          <p style={{ fontSize: scaleFont(15), color: COLORS.secondary, lineHeight: 1.55, margin: '16px 8px 0' }}>
            If this doesn’t settle in a few minutes, our{' '}
            <a href="/support" style={{ color: COLORS.gold }}>support</a> page can help — your
            receipt email guarantees your access.
          </p>
        </div>
      );
    }

    if (purchaseComplete) {
      return (
        <div>
          <div style={{ color: COLORS.gold, display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
            <Icon name="check" size={44} strokeWidth={1.8} />
          </div>
          <h2 style={{ fontSize: scaleFont(27), color: COLORS.gold, fontWeight: 400, textAlign: 'center', margin: '0 0 8px', textShadow: GOLD_HALO }}>
            Beloved Plus is active
          </h2>
          <p role="status" style={{ fontSize: scaleFont(18), color: COLORS.cream, textAlign: 'center', lineHeight: 1.6, margin: '0 0 24px' }}>
            Your support keeps Beloved here for you. Personal prayer is open whenever you need words.
          </p>

          {canUseAccounts() && (
            <div style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: '18px', padding: '18px', marginBottom: '22px' }}>
              {renderAccountPanel('purchase')}
            </div>
          )}

          {!canUseAccounts() && (
            <p style={{ fontSize: scaleFont(16), color: COLORS.secondary, textAlign: 'center', lineHeight: 1.55, margin: '0 0 22px' }}>
              Account protection is temporarily unavailable, but Plus remains active on this device.
            </p>
          )}

          {purchaseRedemptionUrl && (
            <div style={{ background: 'rgba(212,175,55,0.08)', border: `1px solid ${COLORS.gold}`, borderRadius: '16px', padding: '16px', marginBottom: '18px', textAlign: 'center' }}>
              <p style={{ fontSize: scaleFont(16), color: COLORS.cream, lineHeight: 1.55, margin: '0 0 12px' }}>
                Planning to use Beloved on iPhone? RevenueCat has provided a secure link to carry this purchase into the app.
              </p>
              <a href={purchaseRedemptionUrl} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.gold, fontSize: scaleFont(17), fontWeight: 700 }}>
                Open my iPhone redemption link
              </a>
            </div>
          )}

          {!accountLinked && (
            <p style={{ fontSize: scaleFont(15), color: COLORS.cream, textAlign: 'center', lineHeight: 1.55, margin: '0 6px 16px' }}>
              Without account protection, Plus stays with this browser only. Clearing its data or moving device can remove access; keep your payment receipt so support can help.
            </p>
          )}

          <button
            type="button"
            onClick={continueAfterPurchase}
            style={accountLinked ? primaryButtonStyle : { ...secondaryButtonStyle, border: `1px solid ${COLORS.border}`, background: 'none', color: COLORS.secondary }}
          >
            {accountLinked
              ? paywallContext === 'settings' ? 'Done' : 'Continue to my prayer'
              : 'Use Plus on this device only'}
          </button>
          <p style={{ fontSize: scaleFont(15), color: COLORS.secondary, textAlign: 'center', lineHeight: 1.55, margin: '14px 8px 0' }}>
            You can add or retry account protection in Settings at any time.
          </p>
        </div>
      );
    }

    return (
      <div>
        <div style={{ color: COLORS.gold, display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
          <Icon name="heart" size={40} strokeWidth={1.4} />
        </div>
        <h2 style={{ fontSize: scaleFont(27), color: COLORS.gold, fontWeight: 400, textAlign: 'center', margin: '0 0 8px', textShadow: GOLD_HALO }}>
          Beloved Plus
        </h2>
        <p style={{ fontSize: scaleFont(18), color: COLORS.cream, textAlign: 'center', lineHeight: 1.6, margin: '0 0 24px' }}>
          Prayers written just for you, whenever your heart needs them.
        </p>

        {/* Price choices — the yearly one is highlighted */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px', marginBottom: '22px' }}>
          {plusPlans.map((plan) => {
            const selected = paywallPlan === plan.id;
            return (
              <button
                key={plan.id}
                onClick={() => setPaywallPlan(plan.id)}
                aria-pressed={selected}
                style={{
                  width: '100%',
                  minHeight: plan.best ? '116px' : '94px',
                  padding: plan.best ? '20px' : '18px 20px',
                  borderRadius: '18px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: SERIF,
                  border: `2px solid ${selected ? COLORS.gold : COLORS.border}`,
                  background: selected
                    ? 'linear-gradient(160deg, rgba(212,175,55,0.16) 0%, rgba(212,175,55,0.06) 100%)'
                    : COLORS.bgCard,
                  boxShadow: selected ? '0 0 24px rgba(212,175,55,0.18)' : 'none',
                  display: 'grid',
                  gridTemplateColumns: '28px minmax(0, 1fr)',
                  alignItems: 'center',
                  columnGap: '14px',
                  rowGap: '8px',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: '26px',
                    height: '26px',
                    flexShrink: 0,
                    borderRadius: '50%',
                    border: `2px solid ${COLORS.gold}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COLORS.bg,
                    background: selected ? COLORS.gold : 'transparent',
                  }}
                >
                  {selected && <Icon name="check" size={16} strokeWidth={2.4} />}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '7px' }}>
                    <span
                      className={plan.best ? 'beloved-paywall-main-price' : undefined}
                      style={{
                        fontSize: scaleFont(plan.best ? 34 : 27),
                        color: plan.best ? COLORS.goldLight : COLORS.text,
                        fontWeight: 700,
                        lineHeight: 1,
                        letterSpacing: '-0.5px',
                        textShadow: plan.best ? GOLD_HALO : 'none',
                      }}
                    >
                      {plan.headlinePrice}
                    </span>
                    <span style={{ fontSize: scaleFont(17), color: COLORS.cream, fontWeight: 600 }}>
                      {plan.cadence}
                    </span>
                  </span>
                  <span style={{ display: 'block', fontSize: scaleFont(15), color: COLORS.secondary, marginTop: '8px' }}>
                    {plan.billing}
                  </span>
                </span>
                {plan.best && (
                  <span
                    style={{
                      gridColumn: '2',
                      justifySelf: 'start',
                      fontSize: scaleFont(14),
                      color: COLORS.bg,
                      background: COLORS.gold,
                      borderRadius: '10px',
                      padding: '5px 10px',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                    }}
                  >
                    {plan.saving}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* What you get */}
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
          {features.map((feature) => (
            <li
              key={feature}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}
            >
              <span style={{ color: COLORS.gold, display: 'flex', flexShrink: 0 }}>
                <Icon name="check" size={24} strokeWidth={2} />
              </span>
              <span style={{ fontSize: scaleFont(18), color: COLORS.text, lineHeight: 1.5 }}>{feature}</span>
            </li>
          ))}
        </ul>

        <p style={{ fontSize: scaleFont(15), color: COLORS.secondary, textAlign: 'center', lineHeight: 1.55, margin: '-4px 10px 22px' }}>
          For personal, non-automated prayer. Gentle fair-use safeguards keep Beloved available for everyone.
        </p>

        {canPurchaseOnWeb() && !account && (
          <div style={{ marginBottom: '18px' }}>
            <label htmlFor="beloved-receipt-email" style={{ display: 'block', fontSize: scaleFont(16), color: COLORS.cream, marginBottom: '7px' }}>
              Email for your receipt
            </label>
            <input
              id="beloved-receipt-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={accountEmail}
              onChange={(event) => setAccountEmail(event.target.value)}
              placeholder="you@example.com"
              style={{ width: '100%', minHeight: '56px', boxSizing: 'border-box', borderRadius: '14px', border: `2px solid ${COLORS.border}`, background: COLORS.bgLight, color: COLORS.text, padding: '12px 14px', fontFamily: SERIF, fontSize: scaleFont(18) }}
            />
            <p style={{ fontSize: scaleFont(14), color: COLORS.secondary, lineHeight: 1.5, margin: '8px 2px 0' }}>
              RevenueCat uses this for checkout and your receipt. Beloved does not create an account unless you explicitly choose that after payment.
            </p>
          </div>
        )}

        {paywallMessage && (
          <p
            style={{
              fontSize: scaleFont(17),
              color: COLORS.cream,
              textAlign: 'center',
              lineHeight: 1.6,
              background: COLORS.bgCard,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '14px',
              padding: '14px 16px',
              margin: '0 0 18px',
            }}
          >
            {paywallMessage}
          </p>
        )}

        <button
          onClick={startBelovedPlus}
          disabled={paywallBusy}
          style={{ ...primaryButtonStyle, opacity: paywallBusy ? 0.62 : 1 }}
        >
          {paywallBusy ? 'Opening secure checkout…' : selectedPlan.checkoutLabel || 'Continue to checkout'}
        </button>

        <button
          onClick={paywallContext === 'settings' ? closeSettingsPaywall : dismissPaywallWithPrayer}
          disabled={paywallBusy}
          style={{
            ...secondaryButtonStyle,
            marginTop: '14px',
            border: `1px solid ${COLORS.border}`,
            background: 'none',
            color: COLORS.secondary,
          }}
        >
          {paywallContext === 'settings' ? 'Not now' : 'Maybe later'}
        </button>

        <button
          onClick={restoreBelovedPlus}
          disabled={paywallBusy}
          style={{
            width: '100%',
            minHeight: '48px',
            marginTop: '10px',
            background: 'none',
            border: 'none',
            color: COLORS.gold,
            fontSize: scaleFont(17),
            fontFamily: SERIF,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Restore a purchase
        </button>

        <p style={{ fontSize: scaleFont(15), color: COLORS.secondary, textAlign: 'center', lineHeight: 1.6, margin: '18px 4px 0' }}>
          Renews automatically until cancelled. Payment is handled {canPurchaseOnWeb() ? 'securely by RevenueCat' : 'by the App Store'}.
          {canPurchaseOnWeb() && APPLE_PAY_DOMAIN_VERIFIED &&
            ' Apple Pay or card is offered when supported by your device.'}
          {' '}By continuing, you agree to the{' '}
          <a href={`${PUBLIC_SITE_ORIGIN}/terms`} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.gold }}>Terms</a>
          {' '}and acknowledge the{' '}
          <a href={`${PUBLIC_SITE_ORIGIN}/privacy`} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.gold }}>Privacy Policy</a>.
        </p>
      </div>
    );
  };

  // ============================================
  // Screen: Settings
  // ============================================
  const renderSettings = () => {
    const showReminders = isNotificationsSupported();

    // A large, calm card wrapper used by every settings item.
    const cardStyle = {
      background: COLORS.bgCard,
      borderRadius: '18px',
      padding: '22px 20px',
      marginBottom: '18px',
      border: `1px solid ${COLORS.border}`,
    };
    const cardTitleStyle = {
      fontSize: scaleFont(20),
      color: COLORS.cream,
      fontWeight: 400,
      margin: '0 0 8px',
    };
    const cardBodyStyle = {
      fontSize: scaleFont(17),
      lineHeight: 1.6,
      color: COLORS.secondary,
      margin: '0 0 18px',
    };
    const timeInputStyle = {
      width: '100%',
      minHeight: '56px',
      boxSizing: 'border-box',
      borderRadius: '14px',
      border: `2px solid ${COLORS.border}`,
      background: COLORS.bgLight,
      color: COLORS.text,
      padding: '12px 14px',
      fontFamily: SERIF,
      fontSize: scaleFont(18),
      colorScheme: 'dark',
    };

    return (
      <div>
        <h2 style={{ fontSize: scaleFont(24), color: COLORS.cream, fontWeight: 400, margin: '4px 0 20px' }}>Settings</h2>

        {/* Beloved Plus — first, so joining or managing is never hidden. */}
        <div style={cardStyle}>
          <p style={cardTitleStyle}>Beloved Plus</p>
          {tier === 'plus' ? (
            <>
              <p style={cardBodyStyle}>Beloved Plus is active — thank you.</p>
              <button
                type="button"
                onClick={handleManageSubscription}
                disabled={settingsBusy}
                style={{ ...secondaryButtonStyle, opacity: settingsBusy ? 0.7 : 1 }}
              >
                {settingsBusy ? 'One moment...' : 'Manage subscription'}
              </button>
              {settingsMessage && (
                <p role="status" aria-live="polite" style={{ fontSize: scaleFont(17), lineHeight: 1.6, color: COLORS.cream, margin: '16px 0 0' }}>
                  {settingsMessage}
                </p>
              )}
            </>
          ) : (
            <>
              <p style={cardBodyStyle}>
                Never be lost for words — personal prayers whenever you need them.
              </p>
              <p style={{ ...cardBodyStyle, color: COLORS.cream }}>
                £39.99/year or £5.99/month.
              </p>
              <button
                onClick={openPlusFromSettings}
                disabled={settingsBusy}
                style={{ ...primaryButtonStyle, marginBottom: '12px', opacity: settingsBusy ? 0.7 : 1 }}
              >
                Join Beloved Plus
              </button>
              <button
                onClick={handleRestoreInSettings}
                disabled={settingsBusy}
                style={{
                  ...secondaryButtonStyle,
                  cursor: settingsBusy ? 'default' : 'pointer',
                }}
              >
                Restore a purchase
              </button>
              {settingsMessage && (
                <p role="status" aria-live="polite" style={{ fontSize: scaleFont(17), lineHeight: 1.6, color: COLORS.cream, margin: '16px 0 0' }}>
                  {settingsMessage}
                </p>
              )}
            </>
          )}
        </div>

        {/* My account — second: the door to keeping everything safe across devices. */}
        {canUseAccounts() && (
          <div style={cardStyle}>
            <p style={cardTitleStyle}>My account</p>
            {!(account && account.id) && (
              <p style={cardBodyStyle}>Keep your prayers and Beloved Plus safe across devices.</p>
            )}
            {renderAccountPanel('settings')}
          </div>
        )}

        {/* Reading comfort — an explicit web-view reading control for tired eyes. */}
        <div style={cardStyle}>
          <p style={{ ...cardTitleStyle, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span aria-hidden="true" style={{ color: COLORS.gold, fontWeight: 700, letterSpacing: '1px' }}>Aa</span>
            Reading comfort
          </p>
          <p style={cardBodyStyle}>Make the words bigger if they are hard to read.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
            {[
              { key: 'comfortable', label: 'Comfortable' },
              { key: 'large', label: 'Large' },
              { key: 'extraLarge', label: 'Extra large' },
            ].map(({ key, label }) => {
              const chosen = textSize === key;
              return (
                <button
                  key={key}
                  onClick={() => setTextSize(key)}
                  aria-pressed={chosen}
                  style={{
                    minHeight: '56px',
                    padding: '14px 12px',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    fontFamily: SERIF,
                    fontSize: scaleFont(17),
                    fontWeight: chosen ? 700 : 500,
                    border: `2px solid ${chosen ? COLORS.gold : COLORS.border}`,
                    background: chosen ? COLORS.gold : 'transparent',
                    color: chosen ? COLORS.bg : COLORS.text,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Daily reminders — native app only (hidden on the web) */}
        {showReminders && (
          <div style={cardStyle}>
            <p style={cardTitleStyle}>Daily reminders</p>
            <p style={cardBodyStyle}>
              Two quiet notes each day — morning scripture and an evening pause.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: '12px',
                marginBottom: '16px',
              }}
            >
              <label style={{ fontSize: scaleFont(16), color: COLORS.secondary, lineHeight: 1.5 }}>
                Morning
                <input
                  type="time"
                  aria-label="Morning reminder time"
                  value={reminderTimes.morning}
                  onChange={(event) => handleReminderTimeChange('morning', event.target.value)}
                  style={{ ...timeInputStyle, display: 'block', marginTop: '6px' }}
                />
              </label>
              <label style={{ fontSize: scaleFont(16), color: COLORS.secondary, lineHeight: 1.5 }}>
                Evening
                <input
                  type="time"
                  aria-label="Evening reminder time"
                  value={reminderTimes.evening}
                  onChange={(event) => handleReminderTimeChange('evening', event.target.value)}
                  style={{ ...timeInputStyle, display: 'block', marginTop: '6px' }}
                />
              </label>
            </div>
            <button
              onClick={handleToggleReminders}
              disabled={settingsBusy}
              aria-pressed={remindersOn}
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '16px 20px',
                borderRadius: '14px',
                cursor: settingsBusy ? 'default' : 'pointer',
                fontFamily: SERIF,
                fontSize: scaleFont(18),
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                border: `2px solid ${remindersOn ? COLORS.gold : COLORS.border}`,
                background: remindersOn ? COLORS.gold : 'transparent',
                color: remindersOn ? COLORS.bg : COLORS.text,
              }}
            >
              <span>{remindersOn ? 'Reminders are on' : 'Reminders are off'}</span>
              <span style={{ fontWeight: 700 }}>{remindersOn ? 'Turn off' : 'Turn on'}</span>
            </button>
            {reminderMessage && (
              <p
                role="status"
                aria-live="polite"
                style={{ fontSize: scaleFont(16), color: COLORS.secondary, lineHeight: 1.55, margin: '14px 0 0' }}
              >
                {reminderMessage}
              </p>
            )}
          </div>
        )}

        <section aria-label="Device privacy" style={cardStyle}>
          <p style={cardTitleStyle}>Device privacy</p>
          <p style={cardBodyStyle}>
            Saved prayers, saved verses, and prayer intentions stay on this device, even after you sign out.
            Clearing them will not cancel Beloved Plus or delete your account.
          </p>
          {!clearPrivateConfirmation ? (
            <button
              type="button"
              onClick={() => {
                setDevicePrivacyMessage('');
                setClearPrivateConfirmation(true);
              }}
              style={secondaryButtonStyle}
            >
              Clear private content from this device
            </button>
          ) : (
            <section
              aria-label="Confirm clearing private content"
              style={{ padding: '18px', borderRadius: '14px', border: `1px solid ${COLORS.gold}`, background: 'rgba(212,175,55,0.08)' }}
            >
              <p style={{ fontSize: scaleFont(17), color: COLORS.cream, lineHeight: 1.55, margin: '0 0 14px' }}>
                Clear all saved prayers, saved verses, and prayer intentions from this device? This cannot be undone.
              </p>
              <button type="button" onClick={handleClearPrivateContent} style={primaryButtonStyle}>
                Yes, clear private content
              </button>
              <button
                type="button"
                onClick={() => setClearPrivateConfirmation(false)}
                style={{ ...secondaryButtonStyle, marginTop: '10px', border: 'none', background: 'none', color: COLORS.secondary }}
              >
                Keep my content
              </button>
            </section>
          )}
          {devicePrivacyMessage && (
            <p role="status" aria-live="polite" style={{ fontSize: scaleFont(16), color: COLORS.cream, lineHeight: 1.55, margin: '14px 0 0' }}>
              {devicePrivacyMessage}
            </p>
          )}
        </section>

        {/* Private backup — explicit file export/import, never an automatic cloud upload. */}
        <div style={cardStyle}>
          <p style={cardTitleStyle}>Private backup</p>
          <p style={cardBodyStyle}>
            Save a private file containing your kept prayers, verses, intentions, and preferences.
            The file may contain sensitive prayer content, so keep it somewhere you trust.
          </p>
          <button
            onClick={handleExportBackup}
            disabled={backupBusy}
            style={{ ...secondaryButtonStyle, marginBottom: '12px', opacity: backupBusy ? 0.7 : 1 }}
          >
            Export private backup
          </button>
          <button
            onClick={() => backupInputRef.current && backupInputRef.current.click()}
            disabled={backupBusy}
            style={{ ...secondaryButtonStyle, border: `1px solid ${COLORS.border}`, opacity: backupBusy ? 0.7 : 1 }}
          >
            Import a backup
          </button>
          <input
            ref={backupInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleImportBackup}
            aria-label="Choose a Beloved backup file"
            style={{ display: 'none' }}
          />
          {backupMessage && (
            <p
              role="status"
              aria-live="polite"
              style={{ fontSize: scaleFont(16), color: COLORS.cream, lineHeight: 1.55, margin: '14px 0 0' }}
            >
              {backupMessage}
            </p>
          )}
        </div>

        {/* Privacy */}
        <button
          onClick={openPrivacyPolicy}
          style={{ ...rowStyle, marginBottom: '18px' }}
        >
          <span style={{ color: COLORS.gold, display: 'flex' }}>
            <Icon name="book" size={26} />
          </span>
          <span style={{ flex: 1, fontSize: scaleFont(19), color: COLORS.text }}>Privacy</span>
          <span style={{ color: COLORS.gold, display: 'flex' }}>
            <Icon name="chevron" size={22} />
          </span>
        </button>

        {/* About */}
        <div style={cardStyle}>
          <p style={cardTitleStyle}>About</p>
          <p style={{ ...cardBodyStyle, marginBottom: '10px' }}>
            Beloved — a gentle daily companion for prayer and scripture.
          </p>
          <p style={{ fontSize: scaleFont(15), color: COLORS.secondary, margin: 0 }}>Version 1.0</p>
        </div>
      </div>
    );
  };

  // ---- Bottom navigation ----
  const tabs = [
    { id: 'today', icon: 'today', label: 'Today' },
    { id: 'pray', icon: 'pray', label: 'Pray' },
    { id: 'saved', icon: 'saved', label: 'Saved' },
    { id: 'settings', icon: 'gear', label: 'Settings' },
  ];

  // ============================================
  // The Amen moment — a blessing over the prayer, not an animation.
  // A full-screen benediction on the app's own near-black, so when it unmounts
  // onto the (identical) Today background the swap is seamless. Tap anywhere to
  // skip. The visual timing lives in App.css; reduced motion is handled there
  // too (static "Amen." + line, gentle fade, no bloom or scaling).
  // ============================================
  // The once-ever offer after a free user's first personal prayer. A normal
  // screen (no timers), shown only after the benediction has fully finished.
  const renderPlusMoment = () => {
    if (!showPlusMoment) return null;
    const remaining = aiPrayersRemaining(hasPrayerAccount);
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2050,
          background: COLORS.bg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '30px',
          overflowY: 'auto',
        }}
      >
        <p style={{ fontSize: scaleFont(18), color: COLORS.secondary, margin: '0 0 14px' }}>
          That prayer was written just for you.
        </p>
        <h2 style={{ fontSize: scaleFont(30), color: COLORS.gold, fontWeight: 400, margin: '0 0 16px', textShadow: GOLD_HALO }}>
          Never be lost for words again.
        </h2>
        <p style={{ fontSize: scaleFont(18), color: COLORS.cream, lineHeight: 1.65, maxWidth: '420px', margin: '0 0 26px' }}>
          Beloved Plus writes a personal prayer whenever you need one — for every worry, every
          thank-you, every heavy night. From £5.99 a month.
        </p>
        <button
          type="button"
          onClick={() => {
            setShowPlusMoment(false);
            openPlusFromSettings();
          }}
          style={{ ...primaryButtonStyle, maxWidth: '420px' }}
        >
          Yes — I’d like this
        </button>
        <button
          type="button"
          onClick={() => setShowPlusMoment(false)}
          style={{ background: 'none', border: 'none', color: COLORS.secondary, fontFamily: SERIF, fontSize: scaleFont(17), marginTop: '18px', cursor: 'pointer', minHeight: '44px' }}
        >
          Maybe later
        </button>
        {remaining > 0 ? (
          <p style={{ fontSize: scaleFont(14), color: COLORS.secondary, margin: '22px 0 0' }}>
            You still have {remaining} free personal prayer{remaining === 1 ? '' : 's'} this month.
          </p>
        ) : !hasPrayerAccount ? (
          <p style={{ fontSize: scaleFont(14), color: COLORS.secondary, margin: '22px 0 0' }}>
            Or create a free account for {FREE_AI_PRAYER_LIMIT} personal prayers a month.
          </p>
        ) : null}
      </div>
    );
  };

  // The Plus blessing — the Amen moment's golden language for the instant
  // someone has just supported Beloved. Tap anywhere to continue.
  const renderPlusBlessing = () => {
    if (!showPlusBlessing) return null;
    const finish = () => setShowPlusBlessing(false);
    return (
      <div
        className="beloved-amen-overlay"
        onClick={finish}
        role="button"
        tabIndex={0}
        aria-label="Beloved Plus is yours. Tap to continue."
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') finish();
        }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2100,
          background: COLORS.bg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '30px',
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        <div
          className="beloved-amen-content"
          style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <div
            aria-hidden="true"
            className="beloved-amen-bloom"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '340px',
              height: '340px',
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.30) 0%, rgba(212,175,55,0.10) 45%, rgba(212,175,55,0) 70%)',
            }}
          />
          <p className="beloved-blessing-line beloved-blessing-line-1" style={{ fontSize: scaleFont(24), color: COLORS.cream, margin: '0 0 14px' }}>
            Thank you.
          </p>
          <h2 className="beloved-blessing-line beloved-blessing-line-2" style={{ fontSize: scaleFont(32), color: COLORS.gold, fontWeight: 400, margin: '0 0 16px', textShadow: GOLD_HALO }}>
            Beloved Plus is yours.
          </h2>
          <p className="beloved-blessing-line beloved-blessing-line-3" style={{ fontSize: scaleFont(18), color: COLORS.secondary, margin: 0 }}>
            May every prayer you need find its words.
          </p>
        </div>
      </div>
    );
  };

  const renderAmen = () => {
    if (amenLine == null) return null;
    return (
      <div
        className="beloved-amen-overlay"
        onClick={finishAmen}
        role="button"
        tabIndex={0}
        aria-label="Amen. Tap to continue."
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') finishAmen();
        }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2000,
          background: COLORS.bg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '30px',
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        <div
          className="beloved-amen-content"
          style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {/* Soft golden bloom + a fine gold cross, behind the words. */}
          <div
            aria-hidden="true"
            className="beloved-amen-bloom"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '340px',
              height: '340px',
              marginTop: '-190px',
              marginLeft: '-170px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(212,175,55,0.55) 0%, rgba(212,175,55,0.18) 38%, rgba(212,175,55,0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <div
            aria-hidden="true"
            className="beloved-amen-cross"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-184px',
              marginLeft: '-14px',
              color: COLORS.gold,
              pointerEvents: 'none',
            }}
          >
            <Icon name="cross" size={28} strokeWidth={1.4} color={COLORS.gold} />
          </div>

          <h2
            className="beloved-amen-title"
            style={{
              fontSize: scaleFont(40),
              fontWeight: 400,
              color: COLORS.gold,
              fontFamily: SERIF,
              margin: '0 0 14px',
              textShadow: GOLD_HALO,
            }}
          >
            Amen.
          </h2>
          <p
            className="beloved-amen-line"
            style={{
              fontSize: scaleFont(19),
              fontStyle: 'italic',
              color: COLORS.cream,
              fontFamily: SERIF,
              lineHeight: 1.6,
              margin: 0,
              maxWidth: '320px',
            }}
          >
            {amenLine}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      {showWelcome && welcomeImage && <WelcomeScreen image={welcomeImage} onComplete={handleWelcomeComplete} />}

      <div
        style={{
          minHeight: '100vh',
          background: COLORS.bg,
          color: COLORS.text,
          fontFamily: SERIF,
          paddingBottom: textSize === 'extraLarge' ? '150px' : '110px',
        }}
      >
        <header
          style={{
            padding: 'calc(22px + env(safe-area-inset-top)) 24px 16px',
            textAlign: 'center',
            borderBottom: `1px solid ${COLORS.border}`,
            background: `linear-gradient(180deg, ${COLORS.bgCard} 0%, ${COLORS.bg} 100%)`,
          }}
        >
          <h1 style={{ fontSize: scaleFont(24), fontWeight: 400, color: COLORS.gold, margin: 0, letterSpacing: '6px', textShadow: GOLD_HALO }}>BELOVED</h1>
        </header>

        <main
          aria-hidden={helpStep !== null ? 'true' : undefined}
          style={{ width: '100%', minWidth: 0, padding: '22px 20px', maxWidth: '520px', margin: '0 auto', boxSizing: 'border-box', overflowX: 'hidden' }}
        >
          {activeTab === 'today' && renderToday()}
          {activeTab === 'pray' && renderPray()}
          {activeTab === 'saved' && renderSaved()}
          {activeTab === 'settings' && renderSettings()}
        </main>

        <nav
          aria-hidden={helpStep !== null ? 'true' : undefined}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: COLORS.bgCard,
            borderTop: `1px solid ${COLORS.border}`,
            display: 'flex',
            justifyContent: 'space-around',
            paddingTop: '10px',
            paddingBottom: 'max(14px, env(safe-area-inset-bottom))',
          }}
        >
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                aria-label={tab.label}
                aria-current={active ? 'page' : undefined}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '5px',
                  flex: 1,
                  minWidth: 0,
                  minHeight: textSize === 'extraLarge' ? '82px' : '56px',
                  padding: textSize === 'extraLarge' ? '8px 4px' : '6px 8px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: active ? COLORS.gold : COLORS.secondary,
                  fontFamily: SERIF,
                }}
              >
                <Icon name={tab.icon} size={26} strokeWidth={active ? 2 : 1.7} />
                <span style={{ fontSize: scaleFont(15), fontWeight: active ? 700 : 500, lineHeight: 1.15, textAlign: 'center' }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </nav>

        {renderReader()}
        {renderHelp()}
        {renderAmen()}
        {renderPlusBlessing()}
        {renderPlusMoment()}
      </div>
    </>
  );
}

// --------------------------------------------
// Shared overlay styles (full-screen journeys and reader)
// --------------------------------------------
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  background: COLORS.bg,
  overflowY: 'auto',
};

const overlayInnerStyle = {
  maxWidth: '520px',
  margin: '0 auto',
  padding: 'calc(20px + env(safe-area-inset-top)) 20px max(40px, env(safe-area-inset-bottom))',
  minHeight: '100%',
  boxSizing: 'border-box',
};

const backButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: '48px',
  padding: '10px 8px',
  marginBottom: '10px',
  background: 'none',
  border: 'none',
  color: COLORS.gold,
  get fontSize() {
    return scaleFont(18);
  },
  fontFamily: SERIF,
  cursor: 'pointer',
};
